import { createBrowserRouter, Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Home } from "./Home";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, Check, Menu, Send, Monitor, Moon, Sun, X, CheckCircle2, AlertTriangle, Info, XCircle, MinusCircle, MessageSquare } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import { trackEvent } from "./analytics";
import { buildReportModel, filterGroups, gateBannerText, previewSummary, readScoringV2, TIER_LABELS, EVIDENCE_LABELS, AUDIENCE_LABELS } from "./report/reportModel";
import type { ReportSignalStatus, DetectionSignal, DetectionData, EvidenceStatus, Signal, Rec } from "./report/reportModel";
import { useReportDownload } from "./report/useReportDownload";
import type { RunCitationsResult } from "./report/useReportDownload";
import { DownloadReportButton } from "./report/DownloadReportButton";
import { CITATIONS_ENABLED } from "./report/flags";
import { CookieBanner } from "./CookieBanner";
import { openCookieSettings } from "./consent";
import { BOT_BLOCKED_TITLE, botBlockedDescription } from "./botBlockedCopy";
const logo = `${process.env.PUBLIC_URL}/logo.png`;

// N-08: keep transient / non-canonical pages (404, empty results) out of search
// indexes. This is an SPA, so we can't set an HTTP header — instead we inject a
// <meta name="robots" content="noindex"> while the page is mounted and remove it
// on unmount so it never leaks onto indexable pages.
export function useNoindex() {
  useEffect(() => {
    // N-15: index.html ships a static <meta name="robots" content="index,
    // follow">. This used to just append a second, conflicting meta tag
    // instead of replacing it, so every /results and /scan page carried both
    // "index, follow" and "noindex" at once. Update the existing tag in
    // place (and restore its original value on unmount) instead.
    const existing = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const previousContent = existing?.content;
    let created: HTMLMetaElement | null = null;
    if (existing) {
      existing.content = "noindex";
    } else {
      created = document.createElement("meta");
      created.name = "robots";
      created.content = "noindex";
      document.head.appendChild(created);
    }
    return () => {
      if (created) {
        created.remove();
      } else if (existing && previousContent !== undefined) {
        existing.content = previousContent;
      }
    };
  }, []);
}

// Report signal status → icon. Four distinct SHAPES so each state is legible on
// its own, all drawn in the logo cement color (no warm green/amber/red):
//   pass    → check-circle (verified / good)
//   warn    → triangle     (present but needs improvement)
//   neutral → minus-circle (informational / optional; absence is not a failure)
//   fail    → x-circle     (missing / blocking)
// Note: neutral deliberately does NOT use the Info (ⓘ) glyph — that glyph is
// reserved for the tooltip trigger beside every row, so reusing it here would
// read as "more info" rather than "optional / not applicable".
function SignalStatusIcon({ status, className }: { status: ReportSignalStatus; className?: string }) {
  const cls = className ?? "size-4 shrink-0";
  // Monochrome by design: every icon uses the logo cement color. Meaning is
  // carried by the distinct SHAPE, not by color, to fit the site's
  // black/white/cement palette.
  switch (status) {
    case "pass":
      return <CheckCircle2 className={cls} style={{ color: "var(--accent)" }} strokeWidth={2} aria-label="Pass" />;
    case "warn":
      return <AlertTriangle className={cls} style={{ color: "var(--accent)" }} strokeWidth={2} aria-label="Needs improvement" />;
    case "neutral":
      return <MinusCircle className={cls} style={{ color: "var(--accent)" }} strokeWidth={2} aria-label="Optional" />;
    default:
      return <XCircle className={cls} style={{ color: "var(--accent)" }} strokeWidth={2} aria-label="Not found" />;
  }
}

// Shared pill treatment for signal metadata badges. Comfortable padding so the
// label sits in the middle of the pill rather than feeling boxed-in.
const BADGE_BASE =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--line-soft)] bg-[var(--surface-2)] px-2.5 py-1 text-[10px] font-medium leading-none tracking-[-.02em]";

// Per-status glyph so each evidence state is visually distinct — "mapped" is a
// weaker confirmation than "verified" (matched via llms.txt content rather than
// a direct probe) and must not borrow the verified ✓.
//   verified     → ✓  direct probe confirmed
//   mapped       → ◉  circle-with-dot: matched via mapping, not a hard verify
//   advertised   → ⚠  claimed (header/reference) but not validated
//   experimental → ◇  emerging standard, not scored
//   not_verified → ✗  not found on tested paths
const EVIDENCE_GLYPHS: Record<EvidenceStatus, string> = {
  verified: "✓",
  mapped: "◉",
  advertised: "⚠",
  experimental: "◇",
  not_verified: "✗",
};

// Small pill next to a signal label showing HOW it was detected. All-cement to
// match the palette; meaning comes from the glyph + label, not color.
function EvidenceBadge({ signal }: { signal?: DetectionSignal }) {
  if (!signal) return null;
  const affirmative = signal.status === "verified" || signal.status === "mapped" || signal.status === "advertised";
  const color = affirmative ? "var(--accent)" : "var(--ink-soft)";
  return (
    <span className={BADGE_BASE} style={{ color }}>
      <span aria-hidden="true">{EVIDENCE_GLYPHS[signal.status]}</span>
      {EVIDENCE_LABELS[signal.status]}
    </span>
  );
}

// Small muted pill showing who benefits from a signal (AI search / Coding agents / Both).
function AudienceBadge({ audience }: { audience?: string }) {
  if (!audience) return null;
  return <span className={`${BADGE_BASE} text-[var(--ink-soft)]`}>{AUDIENCE_LABELS[audience] || audience}</span>;
}

// Small muted pill showing how much a missing signal matters (e.g. "Low–medium
// impact"). Restored from the pre-redesign UI, where it sat beside the audience
// pill on failing structured-data signals.
function ImpactBadge({ impact }: { impact?: string }) {
  if (!impact) return null;
  return <span className={`${BADGE_BASE} text-[var(--ink-soft)] opacity-80`}>{impact} impact</span>;
}

// The ⓘ beside every finding. Uses MUI Tooltip (same as the production UI) so it
// renders in a portal above the item — never trapped in the flex row, never
// collapsing to one word per line, never pushing sibling text.
function InfoHint({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <Tooltip
      title={text}
      placement="top"
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 260,
            bgcolor: "var(--panel-bg)",
            color: "var(--panel-fg)",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: "-0.01em",
            px: 1.5,
            py: 1,
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-float)",
          },
        },
        arrow: { sx: { color: "var(--panel-bg)" } },
      }}
    >
      <span tabIndex={0} role="button" aria-label={text} className="inline-flex cursor-help text-[var(--muted)] outline-none transition-colors hover:text-[var(--ink)] focus-visible:text-[var(--ink)]">
        <Info className="size-3" strokeWidth={2} aria-hidden="true" />
      </span>
    </Tooltip>
  );
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://5gg6ce9y9e.execute-api.us-east-1.amazonaws.com";
const AuditAllowanceContext = createContext<{ remaining: number | null } | null>(null);

export function AuditAllowanceProvider({ children }: { children: ReactNode }) {
  const [remaining, setRemaining] = useState<number | null>(null);
  const fetchUsage = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/usage`);
      if (response.ok) setRemaining((await response.json()).remaining);
    } catch {
      console.warn("[AuditAllowance] Unable to load audit allowance");
    }
  }, []);

  useEffect(() => {
    fetchUsage();
    window.addEventListener("lensy:usage-changed", fetchUsage);
    return () => window.removeEventListener("lensy:usage-changed", fetchUsage);
  }, [fetchUsage]);

  const value = useMemo(() => ({ remaining }), [remaining]);
  return <AuditAllowanceContext.Provider value={value}>{children}</AuditAllowanceContext.Provider>;
}

export function useAuditAllowance() {
  const context = useContext(AuditAllowanceContext);
  if (!context) throw new Error("useAuditAllowance must be used inside AuditAllowanceProvider");
  return context;
}

function useReveal() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const els = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); } });
    }, { threshold: 0, rootMargin: "0px 0px -8% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);
}

type Theme = "system" | "light" | "dark";
type ViewTransition = { finished: Promise<void>; ready: Promise<void> };
type ThemeTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => ViewTransition;
};
function applyTheme(t: Theme) {
  const nextTheme = t === "dark" || (t === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light";
  const updateTheme = () => {
    document.documentElement.setAttribute("data-theme", nextTheme);
    document.documentElement.style.colorScheme = nextTheme;
  };

  // Do not animate the initial paint or duplicate Strict Mode effect. On a user
  // change, animate the entire document once instead of every nested element.
  if (document.documentElement.getAttribute("data-theme") === nextTheme) {
    updateTheme();
    return;
  }

  const themeDocument = document as ThemeTransitionDocument;
  if (themeDocument.startViewTransition) {
    const transition = themeDocument.startViewTransition.call(document, updateTheme);
    // When a rapid second toggle interrupts an in-flight transition, the browser
    // rejects these promises with "Transition was skipped". That is expected —
    // swallow it so it never surfaces as an uncaught runtime error.
    transition.finished.catch(() => { });
    transition.ready.catch(() => { });
  } else {
    updateTheme();
  }
}
function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem("theme");
    return saved === "light" || saved === "dark" || saved === "system" ? saved : "system";
  });
  useLayoutEffect(() => {
    applyTheme(theme);
    localStorage.setItem("theme", theme);
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);
  return { theme, setTheme };
}

const themeIcon: Record<Theme, LucideIcon> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const options: Theme[] = ["system", "light", "dark"];
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => { document.removeEventListener("mousedown", closeOnOutsideClick); document.removeEventListener("keydown", closeOnEscape); };
  }, []);
  const labels: Record<Theme, [string, string]> = {
    system: ["System", "Follow your device"],
    light: ["Light", "Always use light"],
    dark: ["Dark", "Always use dark"],
  };
  const ActiveThemeIcon = themeIcon[theme];
  return <div ref={pickerRef} className="relative">
    <button type="button" aria-label="Change color theme" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((current) => !current)} className={`theme-trigger flex size-8 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface)] text-[var(--ink-soft)] transition-colors hover:border-[var(--accent)] hover:text-[var(--ink)] ${open ? "is-open" : ""}`}>
      <ActiveThemeIcon className="size-3.5" strokeWidth={1.65} aria-hidden="true" />
    </button>
    {open && <div role="dialog" aria-label="Color theme" className="popup-shadow theme-menu absolute right-0 top-[calc(100%+10px)] z-30 w-52 rounded-[var(--radius-md)] border border-[var(--line-strong)] bg-[var(--bg)] p-2">
      <div className="px-2 pb-2 pt-1"><p className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">Appearance</p></div>
      <div role="radiogroup" aria-label="Color theme" className="space-y-0.5">
        {options.map((opt) => {
          const active = theme === opt;
          const [label, detail] = labels[opt];
          const ThemeOptionIcon = themeIcon[opt];
          return <button key={opt} type="button" role="radio" aria-checked={active} onClick={() => { setTheme(opt); setOpen(false); }} className={`theme-choice flex w-full items-center gap-3 px-2 py-2 text-left transition-colors ${active ? "bg-[var(--surface-2)] text-[var(--ink)]" : "text-[var(--ink-soft)] hover:bg-[var(--surface)] hover:text-[var(--ink)]"}`}>
            <span className={`grid size-7 shrink-0 place-items-center rounded-[var(--radius-xs)] border ${active ? "border-[var(--accent)] bg-[var(--ink)] text-[var(--bg)]" : "border-[var(--line)]"}`}><ThemeOptionIcon className="size-3.5" strokeWidth={1.65} aria-hidden="true" /></span>
            <span className="min-w-0 flex-1"><span className="block text-[12px] font-medium tracking-[-.02em]">{label}</span><span className="mt-0.5 block text-[10px] font-medium tracking-[-.02em] text-[var(--muted)]">{detail}</span></span>
            <span aria-hidden="true" className={`text-[12px] font-medium tracking-[-.025em] ${active ? "text-[var(--accent)]" : "opacity-0"}`}>✓</span>
          </button>;
        })}
      </div>
    </div>}
  </div>;
}

export function NavLink({ to, label, badge, onNavigate }: { to: string; label: string; badge?: string; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(to + "/");
  // Badge tints use explicit color-mix() values, NOT Tailwind opacity modifiers
  // like bg-[var(--bg)]/15. Tailwind emits those with a nested @supports block,
  // and CRA's production CSS minifier merges that rule into the plain
  // bg-[var(--bg)] rule — which then rendered every page background at 15%
  // opacity in production (light theme went dark grey). Dev builds don't
  // minify, so it only showed up on gamma/prod.
  return <Link to={to} onClick={onNavigate} data-nav-active={active} className={`nav-item relative z-10 inline-flex items-center gap-1.5 rounded-[var(--radius-xs)] px-2.5 py-1.5 ${active ? "is-active text-[var(--bg)]" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`} aria-current={active ? "page" : undefined}>{label}{badge && <span className={`inline-flex items-center rounded-full px-1.5 py-[3px] text-[8px] font-semibold uppercase leading-none tracking-[.09em] ${active ? "bg-[color-mix(in_srgb,var(--bg)_15%,transparent)] text-[var(--bg)]" : "bg-[color-mix(in_srgb,var(--accent)_12%,transparent)] text-[var(--accent)]"}`}>{badge}</span>}</Link>;
}

export function NavRail() {
  const { pathname } = useLocation();
  const railRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });
  useLayoutEffect(() => {
    const updateIndicator = () => {
      const rail = railRef.current;
      const active = rail?.querySelector<HTMLElement>("[data-nav-active='true']");
      if (!rail || !active) { setIndicator((current) => ({ ...current, ready: false })); return; }
      const railBounds = rail.getBoundingClientRect();
      const activeBounds = active.getBoundingClientRect();
      setIndicator({ left: activeBounds.left - railBounds.left, width: activeBounds.width, ready: true });
    };
    const animationFrame = window.requestAnimationFrame(updateIndicator);
    window.addEventListener("resize", updateIndicator);
    const resizeObserver = new ResizeObserver(updateIndicator);
    if (railRef.current) resizeObserver.observe(railRef.current);
    return () => { window.cancelAnimationFrame(animationFrame); window.removeEventListener("resize", updateIndicator); resizeObserver.disconnect(); };
  }, [pathname]);
  return <div ref={railRef} className="nav-rail relative flex min-w-max items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] p-1">
    <span aria-hidden="true" className={`nav-indicator ${indicator.ready ? "is-ready" : ""}`} style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }} />
    <NavLink to="/" label="Lensy" badge="Beta" />
    <NavLink to="/how-it-works" label="How it works" />
    <NavLink to="/education" label="Education" />
    <NavLink to="/contact" label="Contact" onNavigate={() => trackEvent("contact_link_clicked", { source: "header" })} />
  </div>;
}

export function MobileNav({ remaining }: { remaining: number }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);
  // T-11: the desktop NavRail shows a "Beta" badge on Lensy; the mobile menu
  // never carried one at all, so mobile users saw no beta indicator.
  const links: [string, string, string?][] = [
    ["/", "Lensy", "Beta"],
    ["/how-it-works", "How it works"],
    ["/education", "Education"],
    ["/contact", "Contact"],
  ];

  return <div className="relative md:hidden">
    <button type="button" aria-label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} aria-controls="mobile-navigation" onClick={() => setOpen((current) => !current)} className="theme-trigger grid size-8 place-items-center rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface)] text-[var(--ink-soft)] hover:border-[var(--accent)] hover:text-[var(--ink)]">
      {open ? <X className="size-3.5" strokeWidth={1.75} aria-hidden="true" /> : <Menu className="size-3.5" strokeWidth={1.75} aria-hidden="true" />}
    </button>
    {open && <div id="mobile-navigation" className="popup-shadow mobile-nav-menu absolute right-0 top-[calc(100%+10px)] z-30 w-52 overflow-hidden rounded-[var(--radius-md)] border border-[var(--line-strong)] bg-[var(--bg)] p-1.5">
      <div className="space-y-0.5">
        {links.map(([to, label, badge]) => {
          const active = pathname === to || (to !== "/" && pathname.startsWith(to + "/"));
          return <Link key={to} to={to} onClick={to === "/contact" ? () => trackEvent("contact_link_clicked", { source: "mobile-nav" }) : undefined} className={`flex items-center justify-between rounded-[var(--radius-xs)] px-3 py-2.5 text-[13px] font-medium tracking-[-.025em] transition-colors ${active ? "bg-[var(--ink)] text-[var(--bg)]" : "text-[var(--ink-soft)] hover:bg-[var(--surface)] hover:text-[var(--ink)]"}`} aria-current={active ? "page" : undefined}><span className="inline-flex items-center gap-1.5">{label}{badge && <span className={`inline-flex items-center rounded-full px-1.5 py-[3px] text-[8px] font-semibold uppercase leading-none tracking-[.09em] ${active ? "bg-[color-mix(in_srgb,var(--bg)_15%,transparent)] text-[var(--bg)]" : "bg-[color-mix(in_srgb,var(--accent)_12%,transparent)] text-[var(--accent)]"}`}>{badge}</span>}</span>{active && <Check className="size-3.5" strokeWidth={1.8} aria-hidden="true" />}</Link>;
        })}
      </div>
      <p className="mt-1.5 border-t border-[var(--line)] px-3 py-2.5 text-[11px] font-medium tracking-[-.02em] text-[var(--muted)]">Free tier — {remaining} audits left</p>
    </div>}
  </div>;
}

// On a report page, feedback carries the scanned URL and its scores (spec §6.2a).
function currentScanContext(): { auditUrl?: string; scanMetrics?: Record<string, number> } {
  if (!/^\/(results|scan)\b/.test(window.location.pathname)) return {};
  try {
    const report = JSON.parse(window.sessionStorage.getItem("lensy-audit-state") || "null")?.analysisState?.report;
    if (!report) return {};
    const v2 = readScoringV2(report);
    const scanMetrics: Record<string, number> | undefined = v2
      ? { searchScore: v2.search.score, codingScore: v2.coding.score }
      : typeof report.overallScore === "number" ? { overallScore: report.overallScore } : undefined;
    return { auditUrl: report.scope?.url || report.url, scanMetrics };
  } catch {
    return {};
  }
}

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const closeTimer = useRef<number | null>(null);
  const close = () => { if (closeTimer.current) window.clearTimeout(closeTimer.current); setClosing(true); closeTimer.current = window.setTimeout(() => { setOpen(false); setSent(false); setSendError(""); setClosing(false); closeTimer.current = null; }, 180); };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    setSendError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "https://5gg6ce9y9e.execute-api.us-east-1.amazonaws.com"}/console/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: message.trim(), email: email.trim() || undefined, pageUrl: window.location.href, ...currentScanContext() }),
      });
      if (!response.ok) throw new Error(`Feedback request failed with ${response.status}`);
      console.log("[Feedback] Submission accepted");
      setSent(true);
      closeTimer.current = window.setTimeout(close, 1000);
    } catch (error) {
      console.error("[Feedback] Submission failed", error);
      setSendError("We could not send feedback. Please try again.");
    } finally {
      setSending(false);
    }
  };
  useEffect(() => () => { if (closeTimer.current) window.clearTimeout(closeTimer.current); }, []);
  // Below 640px the left-hand button sat on the score cards' text ("3 checks to
  // improve"), so phones get a small icon in the right corner instead (REG-26).
  return <div data-testid="feedback-widget" className="fixed bottom-4 right-4 z-40 flex max-w-[calc(100vw-2rem)] flex-col items-end sm:bottom-5 sm:left-5 sm:right-auto sm:max-w-[calc(100vw-2.5rem)] sm:items-start">
    {open && <div id="feedback-panel" className={`feedback-panel popup-shadow mb-2.5 w-[304px] max-w-full overflow-hidden rounded-[var(--radius-md)] border border-[var(--line-strong)] bg-[var(--bg)] ${closing ? "is-closing" : ""}`}>
      <div className="flex items-center justify-between border-b border-[var(--line)] px-3.5 py-2.5"><p className="text-[13px] font-medium tracking-[-.025em]">Share feedback</p><button type="button" onClick={close} aria-label="Close feedback" className="grid size-6 place-items-center rounded-[var(--radius-xs)] text-[var(--muted)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--ink)]"><X className="size-3" strokeWidth={1.8} aria-hidden="true" /></button></div>
      {sent ? <div role="status" aria-live="polite" className="feedback-success flex items-center gap-3 px-4 py-5"><span className="grid size-7 place-items-center rounded-full bg-[var(--tint)] text-[var(--accent)]"><Check className="size-3.5" strokeWidth={1.9} aria-hidden="true" /></span><p className="text-[13px] font-medium tracking-[-.02em]">Feedback sent.</p></div> : <form onSubmit={submit} className="p-4"><label className="sr-only" htmlFor="feedback-message">Feedback</label><textarea id="feedback-message" value={message} onChange={(event) => setMessage(event.target.value)} placeholder="What would make Lensy more useful?" className="feedback-field min-h-28 w-full resize-y rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[13px] leading-relaxed text-[var(--ink)] outline-none placeholder:text-[var(--placeholder)] focus:border-[var(--accent)]" /><label className="sr-only" htmlFor="feedback-email">Email address</label><input id="feedback-email" value={email} onChange={(event) => setEmail(event.target.value)} type="email" placeholder="Email (optional, for follow-up)" className="feedback-field mt-3 w-full rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[13px] text-[var(--ink)] outline-none placeholder:text-[var(--placeholder)] focus:border-[var(--accent)]" /><button type="submit" disabled={!message.trim() || sending} className="feedback-submit btn-ink mt-3 flex w-full items-center justify-center gap-2 bg-[var(--panel-bg)] px-4 py-2.5 text-[13px] font-medium tracking-[-.02em] text-[var(--panel-fg)] disabled:cursor-not-allowed disabled:opacity-40">{sending ? "Sending…" : <>Send feedback <Send className="size-3" strokeWidth={1.8} aria-hidden="true" /></>}</button>{sendError && <p role="alert" className="mt-3 text-[12px] text-[var(--ink-soft)]">{sendError}</p>}</form>}
    </div>}
    <button type="button" onClick={() => { if (open) { close(); } else { trackEvent("feedback_widget_opened", { pageUrl: window.location.href }); setOpen(true); } }} aria-expanded={open} aria-controls="feedback-panel" aria-label="Feedback" className="feedback-launch inline-flex size-10 items-center justify-center rounded-full border border-[var(--line-strong)] bg-[var(--bg)] text-[13px] font-medium tracking-[-.02em] text-[var(--ink)] sm:size-auto sm:rounded-[var(--radius-sm)] sm:px-3 sm:py-2"><MessageSquare className="size-4 sm:hidden" strokeWidth={1.8} aria-hidden="true" /><span className="hidden sm:inline">Feedback</span></button>
  </div>;
}

export function ShellContent() {
  useReveal();
  const { pathname } = useLocation();
  const { remaining } = useAuditAllowance();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 12);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  return <div className="flex min-h-full flex-col bg-[var(--bg)] text-[var(--ink)]">
    <header className={`shell-header sticky top-0 z-20 border-b border-[var(--line-soft)] ${scrolled ? "is-scrolled" : ""}`}>
      <nav className="relative mx-auto flex max-w-[1240px] items-center px-5 py-2.5 sm:px-8">
        <Link to="/" className="flex items-center gap-2 text-[15px] font-medium tracking-[-.04em]"><img src={logo} alt="Perseverance AI" style={{ width: "clamp(42px, 4.2vw, 55px)", height: "clamp(42px, 4.2vw, 55px)" }} className="logo-mark object-contain transition-transform duration-300 hover:scale-110" />Perseverance AI</Link>
        <div className="absolute left-1/2 hidden -translate-x-1/2 text-[12px] font-medium tracking-[-.025em] md:flex">
          <NavRail />
        </div>
        <div className="ml-auto flex items-center gap-2"><ThemeToggle /><MobileNav remaining={remaining ?? 0} /><span className="hidden rounded-[var(--radius-sm)] border border-[var(--line)] px-2.5 py-1 text-[11px] font-medium tracking-[-.025em] text-[var(--ink-soft)] md:inline-flex">Free tier — {remaining === null ? "checking…" : `${remaining} audits left`}</span></div>
      </nav>
    </header>
    <main className="flex-1"><div key={pathname} className="route-enter"><Outlet /></div></main>
    <FeedbackWidget />
    <CookieBanner />
    <footer className="border-t border-[var(--line)] px-5 py-6 sm:px-8">
      <div className="mx-auto flex max-w-[1400px] flex-col items-center gap-4 text-center">
        <Link to="/" aria-label="Perseverance AI home" className="inline-flex w-full justify-center opacity-80 transition-opacity hover:opacity-100">
          <img src={logo} alt="Perseverance AI" className="logo-mark size-[60px] translate-x-1 object-contain" />
        </Link>
        {/* D-05/D-06: active links read as ink (high-contrast), not the muted --accent
            grey, and hover deepens rather than dims. */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[13px] font-medium text-[var(--ink-soft)]">
          <a href="https://www.linkedin.com/company/getperseverance/" target="_blank" rel="noopener noreferrer" className="link-sweep hover:text-[var(--ink)]">LinkedIn</a>
          <a href="https://x.com/getperseverance" target="_blank" rel="noopener noreferrer" className="link-sweep hover:text-[var(--ink)]">X</a>
          <a href="https://calendly.com/getperseverance" target="_blank" rel="noopener noreferrer" className="link-sweep hover:text-[var(--ink)]">Schedule a conversation</a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12px] text-[var(--muted)]">
          <span>© {new Date().getFullYear()} Perseverance AI. All rights reserved.</span>
          {/* One link for both legal pages: /terms has a side nav to the Privacy Policy. */}
          <Link to="/terms" className="link-sweep text-[var(--ink-soft)] hover:text-[var(--ink)]">Terms and Policy</Link>
          <button type="button" onClick={openCookieSettings} className="link-sweep text-[var(--ink-soft)] hover:text-[var(--ink)]">Cookie settings</button>
        </div>
        <p className="max-w-[640px] text-[11px] leading-relaxed text-[var(--muted)]">Lensy is in beta. Results are generated automatically and may not always be accurate — treat them as guidance, not a definitive audit.</p>
      </div>
    </footer>
  </div>;
}

export function Shell() {
  return (
    <AuditAllowanceProvider>
      <ShellContent />
    </AuditAllowanceProvider>
  );
}

// ---- Scan prototype ----

function getScanProfile(rawUrl: string) {
  const supplied = rawUrl || "docs.example.com";
  let displayUrl = supplied;
  try {
    const parsed = new URL(/^https?:\/\//.test(supplied) ? supplied : `https://${supplied}`);
    displayUrl = (parsed.hostname + parsed.pathname).replace(/^www\./, "").replace(/\/$/, "");
  } catch {
    displayUrl = supplied.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");
  }
  const seed = Array.from(displayUrl).reduce((total, character) => total + character.charCodeAt(0), 0);
  const score = 72 + (seed % 17);
  return { hostname: displayUrl, score };
}

export function ScanReport({
  url,
  analysisState,
  citationData,
  overallScoreData,
  detectionData,
  citationsLoading = false,
  onRunCitations,
  sessionId,
  onScan,
}: {
  url?: string;
  analysisState?: any;
  citationData?: any;
  overallScoreData?: any;
  detectionData?: DetectionData;
  citationsLoading?: boolean;
  onRunCitations?: (trigger?: "card" | "download") => Promise<RunCitationsResult> | void;
  sessionId?: string | null;
  onScan?: (url: string, options?: { forceJsRender?: boolean }) => void;
}) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const urlParam = params.get("url") || analysisState?.report?.url || url || "";
  const { hostname } = getScanProfile(urlParam);
  // "coding" and "search" are the readiness view filtered to one score's checks.
  const [view, setView] = useState<"readiness" | "coding" | "search" | "citations-loading" | "citations" | "recommendations">("readiness");
  const isReadinessView = view === "readiness" || view === "coding" || view === "search";
  const citationRequestStarted = useRef(false);
  useEffect(() => {
    if (citationsLoading) citationRequestStarted.current = true;
    if (view === "citations-loading" && citationRequestStarted.current && !citationsLoading) setView("citations");
  }, [citationsLoading, view]);

  // The scan hit a JS-rendered page: the backend returns an error naming the
  // condition, and we surface the "Proceed with analysis anyway" consent step.
  const isJsRenderPrompt =
    analysisState?.status === "error" &&
    (["js-render-required", "JavaScript-rendered", "JS-rendered"].some((token) => (analysisState.error || "").includes(token)));

  // Fire `js_render_prompt_shown` once each time the consent prompt appears for
  // a URL — this is the top of the JS-render consent funnel (prompt shown →
  // consent given → results). New instrumentation: this flow was never tracked
  // pre-redesign. Keyed on urlParam so a re-scan of the same URL re-arms it.
  const jsRenderPromptTracked = useRef<string | null>(null);
  useEffect(() => {
    if (isJsRenderPrompt && jsRenderPromptTracked.current !== urlParam) {
      jsRenderPromptTracked.current = urlParam;
      trackEvent("js_render_prompt_shown", { url: urlParam });
    }
    if (!isJsRenderPrompt) jsRenderPromptTracked.current = null;
  }, [isJsRenderPrompt, urlParam]);

  let robotsUrl = "#";
  try {
    if (urlParam) {
      const parsedUrlForRobots = new URL(/^https?:\/\//.test(urlParam) ? urlParam : `https://${urlParam}`);
      robotsUrl = `${parsedUrlForRobots.protocol}//${parsedUrlForRobots.hostname}/robots.txt`;
    }
  } catch (e) {
    console.error("Invalid URL for robots.txt:", urlParam);
  }

  // Map a raw scan error (which can be a bare "HTTP 400", an unreachable-host
  // message, or a non-doc rejection) to a human-readable title + description so
  // the error card never shows a raw status code. Falls back to the raw message.
  const describeScanError = (raw?: string): { title: string; description: string } => {
    const msg = (raw || "").trim();
    // REG-18: a bot-protection block is not "unreachable". REG-28: copy lives in botBlockedCopy.ts.
    if (/blocked-by-bot-protection|bot protection blocked/i.test(msg)) {
      const status = (msg.match(/HTTP (\d{3})/) || [])[1] || "403";
      return { title: BOT_BLOCKED_TITLE, description: botBlockedDescription(status) };
    }
    if (/\b400\b/.test(msg)) {
      return { title: "That doesn't look like a valid URL", description: "Please enter a valid documentation URL and try again." };
    }
    if (/\b(429)\b/.test(msg) || /rate.?limit/i.test(msg)) {
      return { title: "Too many requests", description: "The site is rate-limiting our scanner. Wait a moment and try again." };
    }
    // 404 / unreachable / the backend's real "unable to access this URL" message.
    if (/\b(404)\b/.test(msg) || /\b5\d\d\b/.test(msg) || /unable to access|could not (?:be )?reach|unreachable|ENOTFOUND|ECONNREFUSED|timed? ?out|timeout/i.test(msg)) {
      return { title: "We couldn't reach that page", description: "Double-check the URL is correct and publicly reachable, then try again." };
    }
    if (/not a doc|non-doc|documentation/i.test(msg) && !/valid documentation URL/i.test(msg)) {
      return { title: "This doesn't look like a documentation page", description: msg || "Try a URL that points at documentation content." };
    }
    return { title: "Something went wrong", description: msg || "The scan couldn't be completed. Please try again." };
  };

  const report = analysisState?.report;
  const realScore = typeof report?.overallScore === "number" ? report.overallScore : null;
  const hasReport = Boolean(report);

  // N-08 / N-13 / N-14: a visit to /results or /scan with nothing real to show
  // is a dead end (it used to render placeholder/fake data or hang on "Waiting
  // for the completed scan report"). Send those visitors home instead.
  //
  // Timing: LensyApp restores analysisState from sessionStorage in a mount
  // effect, so on the first render after navigating here the status is still
  // 'idle' and the report hasn't rehydrated yet. We must not bounce a
  // just-completed scan during that window. The reliable signal is whether the
  // PERSISTED payload actually carries a report — so we inspect it directly:
  //   - persisted state with a report  -> real scan, let it rehydrate/render
  //   - persisted 'completed' with NO report (interrupted mid-scan, N-13) -> empty
  //   - no persisted state, idle       -> cold direct visit -> empty
  const { pathname: currentPath } = useLocation();
  const status = analysisState?.status;
  const persistedHasReport = (() => {
    try {
      const raw = window.sessionStorage.getItem("lensy-audit-state");
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return Boolean(parsed?.analysisState?.report);
    } catch { return false; }
  })();
  // On these scan-report routes, once we're not actively analyzing and there's
  // no report in memory and none persisted, there is nothing to show.
  const isEmptyResults =
    (currentPath === "/results" || currentPath === "/scan") &&
    !hasReport &&
    !persistedHasReport &&
    status !== "analyzing" &&
    status !== "error"; // an error shows the recovery card, not a redirect
  useNoindex();
  useEffect(() => {
    if (isEmptyResults) navigate("/", { replace: true });
  }, [isEmptyResults, navigate]);

  // Preserve the Figma three-card report layout. Every item below comes from
  // the completed agent report; omitted checks are never represented by mock rows.
  // Signal status mirrors the pre-redesign report: a real 4-state model, not a
  // pass/fail boolean. 'pass' = verified/good, 'warn' = present but sub-optimal,
  // 'neutral' = informational/optional (absence is not a failure), 'fail' = missing.
  // Everything the report shows is computed once in buildReportModel (shared
  // with the Markdown and PDF downloads, H-001) so the page and the files agree.
  const {
    v2, botAccess, botAccessState, botAccessBannerText, signalGroups, groupStatus, resolveImpact, fixForSignal,
    quickWins, deeperImprovements, thingsToWatch, totalRecCount, recIssue, recFix, impactLabel, effortLabel, actionLink,
    aiDisc, availableCitationEngines, citationResults, citationsByTier, citationsUntiered, testedEngineLabel,
    citedCount, citationsReturned, failingSignalCount, failingGroupCount,
  } = useMemo(
    () => buildReportModel({ report, overallScoreData, detectionData, citationData }),
    [report, overallScoreData, detectionData, citationData],
  );
  const reportDownload = useReportDownload({
    input: { report, url: urlParam, sessionId, overallScoreData, detectionData, citationData },
    citationsLoading,
    onRunCitations,
    view,
  });
  const startCitationTest = () => {
    // A failed run (H-001 B2) can be retried from the card.
    if (aiDisc && aiDisc.status !== "error") {
      setView("citations");
      return;
    }
    citationRequestStarted.current = false;
    setView("citations-loading");
    if (!citationsLoading) onRunCitations?.("card");
  };
  const backToReadiness = () => setView("readiness");
  // Clicking a score card shows that score's checks; clicking it again shows all.
  const toggleScore = (key: "coding" | "search") => {
    trackEvent("score_card_clicked", { score: key, active: view !== key });
    setView(view === key ? "readiness" : key);
  };
  const shownGroups = v2 && (view === "coding" || view === "search") ? filterGroups(signalGroups, view) : signalGroups;
  const heading = view === "recommendations" ? "All recommendations" : view === "citations" || view === "citations-loading" ? "Where am I invisible?" : "What Lensy found";

  // A single scored recommendation card: title, impact/effort/category chips,
  // the fix text, an optional "we can help" link, and a code snippet when present.
  const chipClass = "inline-flex items-center rounded-full border border-[var(--line-soft)] bg-[var(--surface-2)] px-2.5 py-1 text-[11px] font-medium leading-none tracking-[-.02em] text-[var(--ink-soft)]";
  const renderRec = (rec: Rec, key: string) => {
    const link = actionLink(rec);
    return (
      <article key={key} className="rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] p-5 transition-colors hover:border-[var(--line-strong)]">
        <p className="text-[15px] font-medium leading-snug tracking-[-.03em] text-[var(--ink)]">{recIssue(rec)}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className={chipClass}>{impactLabel(rec)}</span>
          <span className={chipClass}>{effortLabel(rec)}</span>
          {rec.category && <span className={chipClass}>{rec.category}</span>}
          {rec.points && <span data-testid="rec-points" className={`${chipClass} tabular-nums`}>Points {rec.points}</span>}
        </div>
        {recFix(rec) && <p className="mt-3 text-[13px] leading-relaxed text-[var(--ink-soft)]">{recFix(rec)}</p>}
        {link && <a href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("generate_markdown_link_clicked", { ref: link.href })} className="group mt-2.5 inline-flex items-center gap-1 text-[12px] font-medium tracking-[-.02em] text-[var(--ink)]"><span style={{ textDecoration: "underline", textUnderlineOffset: "3px" }}>{link.label}</span><ArrowRight className="arrow-nudge size-3" strokeWidth={1.8} aria-hidden="true" /></a>}
        {rec.codeSnippet && <pre className="mt-4 overflow-x-auto rounded-[var(--radius-md)] text-[12px] leading-[1.7]" style={{ background: "var(--panel-bg)", color: "var(--panel-fg)", fontFamily: "var(--font-mono, ui-monospace, monospace)", whiteSpace: "pre-wrap", padding: "16px 18px" }}>{rec.codeSnippet}</pre>}
      </article>
    );
  };

  // Citations results as a semantic <table> (T-10): proper thead/th scope/tbody/
  // tr/td for screen readers, an engine-named result column (T-09), and an
  // intent-tier badge per row (T-08). `resultHeader` names the engine tested.
  const renderCitationRow = (result: any) => (
    <tr key={`${result.engine}-${result.query}`} className="border-b border-[var(--line)] align-top last:border-b-0">
      <td className="px-4 py-5">
        <p className="text-[15px] tracking-[-.03em]">“{result.query}”</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {result.tier && <span className={chipClass}>{TIER_LABELS[result.tier]}</span>}
        </div>
        {/* N-16: a long, unbroken citedUrl could push the table wider than its
            overflow-hidden wrapper, silently clipping the Cited pill in the
            next column. Let the URL wrap instead of forcing table width. */}
        {result.citedUrl && <p className="mt-2 break-all text-[12px] leading-relaxed text-[var(--ink-soft)]">Cited URL: {result.citedUrl}</p>}
        {result.competingDomains?.length > 0 && <p className="mt-2 text-[12px] leading-relaxed text-[var(--ink-soft)]">Cited instead: {result.competingDomains.join(", ")}</p>}
      </td>
      <td className="px-4 py-5 text-right align-middle">
        <span className="inline-flex rounded-full border border-[var(--line)] px-3 py-1 text-[11px] font-medium tracking-[-.025em] text-[var(--ink-soft)]">{result.cited ? "Cited" : "Not cited"}</span>
      </td>
    </tr>
  );
  const renderCitationTable = () => (
    // N-16: overflow-hidden silently clipped the Cited pill at narrow widths
    // instead of letting the table scroll; overflow-x-auto is the safety net
    // on top of the break-all fix above.
    <div className="mt-6 overflow-x-auto rounded-[var(--radius-md)] border border-[var(--line)]">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-[var(--line)] text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">
            <th scope="col" className="px-4 py-3 font-medium">Query</th>
            <th scope="col" className="px-4 py-3 text-right font-medium">{testedEngineLabel || "Result"}</th>
          </tr>
        </thead>
        {/* Rows are ordered by intent (high → mid → low, then any untiered), but
            the tier is shown per-row as a capsule — no group-header rows, which
            were visually noisy alongside the capsules. */}
        <tbody>
          {[...citationsByTier.flatMap((g) => g.rows), ...citationsUntiered].map(renderCitationRow)}
        </tbody>
      </table>
    </div>
  );

  // Redirecting an empty /results (N-08) — render nothing to avoid flashing the
  // placeholder report before the navigate effect runs.
  if (isEmptyResults) return null;

  return <section className="mx-auto max-w-[1240px] px-5 pb-20 pt-12 sm:px-8 sm:pb-28 sm:pt-16">
    <div className="mb-10">
      <Link to="/" className="inline-flex items-center gap-2 text-[12px] font-medium tracking-[-.025em] text-[var(--muted)] transition-colors hover:text-[var(--accent)]">
        <ArrowLeft className="size-3" strokeWidth={1.8} aria-hidden="true" />Back to Lensy
      </Link>
    </div>

    {analysisState?.status === 'error' && (
      <div id="scan-error" role="alert" className="mb-8 mt-3 rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--tint)] px-5 py-4 text-[12px] font-medium tracking-[-.02em] text-[var(--ink)] flex flex-col items-start gap-2">
        {isJsRenderPrompt ? (
          <>
            <div className="font-medium text-[15px] tracking-[-.02em] text-[var(--ink)]">This page is rendered by JavaScript</div>
            <div className="text-[13px] text-[var(--ink-soft)] font-normal leading-[1.55] tracking-[-.01em]">Most AI bots (like GPTBot or ClaudeBot) do not execute JavaScript and cannot crawl your website. Consider Server-Side Rendering (SSR) for AI discoverability.</div>
            <button
              onClick={(e) => {
                e.preventDefault();
                // Legacy event kept for continuity + the dedicated consent event
                // that measures the JS-render funnel conversion.
                trackEvent("try_another_url_clicked", { action: "js-render", error: "This page is rendered by JavaScript" });
                trackEvent("js_render_consent_given", { url: urlParam });
                onScan?.(urlParam, { forceJsRender: true });
              }}
              className="mt-2 btn-ink shrink-0 bg-[var(--ink)] px-4 py-2 text-[12px] font-medium tracking-[-.025em] text-[var(--bg)]"
            >
              Proceed with analysis anyway
            </button>
          </>
        ) : (
          (() => {
            const { title, description } = describeScanError(analysisState.error);
            return (
              <>
                <div className="font-medium text-[15px] tracking-[-.02em] text-[var(--ink)]">{title}</div>
                <div className="text-[13px] text-[var(--ink-soft)] font-normal leading-[1.55] tracking-[-.01em]">{description}</div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      trackEvent("try_again_clicked", { url: urlParam, error: analysisState.error });
                      onScan?.(urlParam);
                    }}
                    className="btn-ink shrink-0 bg-[var(--ink)] px-4 py-2 text-[12px] font-medium tracking-[-.025em] text-[var(--bg)]"
                  >
                    Try again
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      // "Try another URL" clears the input by returning home (Home clears session on mount).
                      trackEvent("try_another_url_clicked", { action: "error-recovery", error: analysisState.error });
                      navigate("/");
                    }}
                    className="shrink-0 rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--surface)] px-4 py-2 text-[12px] font-medium tracking-[-.025em] text-[var(--ink)] transition-colors hover:bg-[var(--tint)]"
                  >
                    Try another URL
                  </button>
                  <a
                    href="/contact?ref=feedback"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("report_issue_clicked", { url: urlParam, error: analysisState.error })}
                    className="shrink-0 rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--surface)] px-4 py-2 text-[12px] font-medium tracking-[-.025em] text-[var(--ink)] transition-colors hover:bg-[var(--tint)]"
                  >
                    Report an issue
                  </a>
                </div>
              </>
            );
          })()
        )}
      </div>
    )}

    <div className="flex flex-col gap-5 border-b border-[var(--line)] pb-7 sm:flex-row sm:items-end sm:justify-between">
      <div><p className="text-[12px] font-medium tracking-[-.025em] text-[var(--accent)]">Lensy scan / AI readiness report</p><h1 className="mt-3 text-[clamp(2.4rem,5.5vw,5rem)] font-medium leading-[.93] tracking-[-.07em]">{heading}</h1></div>
      <div className="flex flex-col items-start sm:items-end gap-1.5 text-[12px] font-medium tracking-[-.025em] text-[var(--muted)]">
        <div><span>Scanned </span><span className="text-[var(--ink)]">{hostname}</span></div>
        {(isReadinessView || view === "recommendations") && report?.analysisTime && <div><span>AI readiness Completed in </span><span className="text-[var(--ink)]">{(report.analysisTime / 1000).toFixed(1)}s</span></div>}
        {(view === "citations" || view === "citations-loading") && aiDisc && (aiDisc.analysisTime || aiDisc.processingTime || aiDisc.duration || aiDisc.executionTime || aiDisc.time) && <div><span>AI citations check completed in </span><span className="text-[var(--ink)]">{((aiDisc.analysisTime || aiDisc.processingTime || aiDisc.duration || aiDisc.executionTime || aiDisc.time) / 1000).toFixed(1)}s</span></div>}
      </div>
    </div>

    {v2 ? (
      <div data-testid="score-cards" className={`mt-8 grid gap-3 ${CITATIONS_ENABLED ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {([
          ["search", "AI search", v2.search, "Can AI search (ChatGPT, Perplexity, Claude) find and quote this page?"],
          ["coding", "Coding agents", v2.coding, "Can Claude Code, Cursor and other coding agents fetch and use this page?"],
        ] as const).map(([key, title, score, about]) => <button key={key} data-testid={`score-card-${key}`} aria-pressed={view === key} onClick={() => toggleScore(key)} className={`rounded-[var(--radius-md)] border p-6 text-left transition-colors sm:p-7 ${view === key ? "border-[var(--ink)] bg-[var(--panel-bg)] text-[var(--panel-fg)]" : "border-[var(--line)] bg-[var(--surface)] hover:bg-[var(--surface-2)]"}`}>
          <span className="text-[11px] font-medium tracking-[-.025em] opacity-65">{title}</span>
          <span className="mt-6 block text-[clamp(2.7rem,5vw,4.4rem)] font-medium leading-none tracking-[-.08em]">{score.score}<span className="ml-1 text-[15px] tracking-normal opacity-60">/100</span></span>
          <span className="mt-3 block text-[13px] leading-relaxed opacity-75">{about}</span>
          <span className="mt-2 block text-[12px] leading-relaxed opacity-65">{score.toImprove ? `${score.toImprove} check${score.toImprove === 1 ? "" : "s"} to improve` : "Every check passes"}{view === key ? " · showing these checks" : ""}</span>
        </button>)}
        {CITATIONS_ENABLED && (
          <button onClick={startCitationTest} className={`rounded-[var(--radius-md)] border p-6 text-left transition-colors sm:p-7 ${view === "citations" || view === "citations-loading" ? "border-[var(--ink)] bg-[var(--panel-bg)] text-[var(--panel-fg)]" : "border-[var(--line)] bg-[var(--surface)] hover:bg-[var(--surface-2)]"}`}>
            {citationResults.length ? (<>
              <span className="text-[11px] font-medium tracking-[-.025em] opacity-65">AI citations</span>
              <span className="mt-6 block text-[clamp(2.7rem,5vw,4.4rem)] font-medium leading-none tracking-[-.08em]">{citedCount}/{citationResults.length}</span>
              <span className="mt-3 block text-[13px] leading-relaxed opacity-75">Cited in {citedCount} of {citationResults.length} tested queries</span>
            </>) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, width: '100%', minHeight: 80 }}>
                {citationsLoading ? <span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: '50%', borderWidth: 3, borderStyle: 'solid', borderColor: 'var(--accent, #697075)', borderTopColor: 'transparent', animation: 'citationspin .8s linear infinite', flexShrink: 0 }} /> : <svg width="32" height="36" viewBox="0 0 24 24" style={{ flexShrink: 0, opacity: 0.5 }}><path d="M8 5.14v14l11-7-11-7z" fill="currentColor" /></svg>}
                <div>
                  <span style={{ display: 'block', fontSize: 17, fontWeight: 500, letterSpacing: '-0.025em' }}>AI Citations</span>
                  <span style={{ display: 'block', marginTop: 4, fontSize: 13, opacity: 0.65 }}>{citationsLoading ? "Testing citations with AI search" : "Run citation check"}</span>
                </div>
              </div>
            )}
            <style>{`@keyframes citationspin{to{transform:rotate(360deg)}}`}</style>
          </button>
        )}
      </div>
    ) : (
      <>
        {hasReport && <div data-testid="legacy-report-note" className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-[12px] font-medium tracking-[-.02em] text-[var(--ink-soft)]">
          <span>This report was scored before Lensy split readiness into two scores, AI search and Coding agents. Re-scan to see the new scores.</span>
          {onScan && urlParam && <button onClick={() => { trackEvent("legacy_rescan_clicked", { url: urlParam }); onScan(urlParam); }} className="btn-ink shrink-0 bg-[var(--ink)] px-3 py-1.5 text-[12px] font-medium tracking-[-.025em] text-[var(--bg)]">Re-scan</button>}
        </div>}
        <div className={`${hasReport ? "mt-3" : "mt-8"} grid gap-3 ${CITATIONS_ENABLED ? "sm:grid-cols-2" : ""}`}>
          <button onClick={backToReadiness} className={`rounded-[var(--radius-md)] border p-6 text-left transition-colors sm:p-7 ${view === "readiness" || view === "recommendations" ? "border-[var(--ink)] bg-[var(--panel-bg)] text-[var(--panel-fg)]" : "border-[var(--line)] bg-[var(--surface)] hover:bg-[var(--surface-2)]"}`}>
            <span className="text-[11px] font-medium tracking-[-.025em] opacity-65">AI readiness</span>
            <span className="mt-6 block text-[clamp(2.7rem,5vw,4.4rem)] font-medium leading-none tracking-[-.08em]">{realScore ?? "—"}<span className="ml-1 text-[15px] tracking-normal opacity-60">/100</span></span>
            <span className="mt-3 block text-[13px] leading-relaxed opacity-75">{hasReport ? `${failingSignalCount} signal${failingSignalCount === 1 ? "" : "s"} to improve across ${failingGroupCount} categor${failingGroupCount === 1 ? "y" : "ies"}` : "Waiting for the completed scan report"}</span>
          </button>
          {CITATIONS_ENABLED && (
            <button onClick={startCitationTest} className={`rounded-[var(--radius-md)] border p-6 text-left transition-colors sm:p-7 ${view === "citations" || view === "citations-loading" ? "border-[var(--ink)] bg-[var(--panel-bg)] text-[var(--panel-fg)]" : "border-[var(--line)] bg-[var(--surface)] hover:bg-[var(--surface-2)]"}`}>
              {citationResults.length ? (<>
                <span className="text-[11px] font-medium tracking-[-.025em] opacity-65">AI citations</span>
                <span className="mt-6 block text-[clamp(2.7rem,5vw,4.4rem)] font-medium leading-none tracking-[-.08em]">{citedCount}/{citationResults.length}</span>
                <span className="mt-3 block text-[13px] leading-relaxed opacity-75">Cited in {citedCount} of {citationResults.length} tested queries</span>
              </>) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, width: '100%', minHeight: 80 }}>
                  {citationsLoading ? <span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: '50%', borderWidth: 3, borderStyle: 'solid', borderColor: 'var(--accent, #697075)', borderTopColor: 'transparent', animation: 'citationspin .8s linear infinite', flexShrink: 0 }} /> : <svg width="32" height="36" viewBox="0 0 24 24" style={{ flexShrink: 0, opacity: 0.5 }}><path d="M8 5.14v14l11-7-11-7z" fill="currentColor" /></svg>}
                  <div>
                    <span style={{ display: 'block', fontSize: 17, fontWeight: 500, letterSpacing: '-0.025em' }}>AI Citations</span>
                    <span style={{ display: 'block', marginTop: 4, fontSize: 13, opacity: 0.65 }}>{citationsLoading ? "Testing citations with AI search" : "Run citation check"}</span>
                  </div>
                </div>
              )}
              <style>{`@keyframes citationspin{to{transform:rotate(360deg)}}`}</style>
            </button>
          )}
        </div>
      </>
    )}

    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-[var(--line)] pb-5 text-[12px] font-medium tracking-[-.025em]">
      {(overallScoreData?.docConfidence || report?.docConfidence) ? (
        (() => {
          // N-12: prefer the live overallScore card, but fall back to the
          // persisted report.docConfidence so the chip (and the "Recovered via
          // markdown alternate" note) survives a reload / reopen from report.json.
          const docConf = overallScoreData?.docConfidence || report?.docConfidence;
          const rawScore = docConf.score;
          const pct = rawScore <= 1 ? Math.round(rawScore * 100) : Math.round(rawScore);
          const message = docConf.label || (pct >= 75 ? "Likely a doc page" : pct >= 40 ? "May be a doc page" : "Unlikely a doc page");
          // When the page failed the HTML confidence gate but was rescued via its
          // markdown twin, the percentage is meaningless (it scored empty SPA
          // HTML). Show a qualitative note instead of a contradictory "0%".
          const markdownRescued = Boolean(docConf.markdownRescued);

          return (
            <div className="flex items-center gap-4">
              <span className="rounded-full border border-[var(--line)] bg-[var(--surface-2)] px-3 py-1 text-[var(--ink)]">
                {markdownRescued ? "Recovered via markdown alternate" : `${message} (${pct}%)`}
              </span>
              <Tooltip
                placement="bottom"
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={3000}
                title={
                  <ul className="list-outside list-disc space-y-1.5" style={{ paddingLeft: "1.15rem", margin: 0 }}>
                    {(docConf.signals || []).map((sig: string, i: number) => (
                      <li key={i}>{sig}</li>
                    ))}
                  </ul>
                }
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 340,
                      bgcolor: "var(--panel-bg)",
                      color: "var(--panel-fg)",
                      fontSize: "13px",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      textAlign: "left",
                      p: 2,
                      borderRadius: "var(--radius-md)",
                      boxShadow: "var(--shadow-float)",
                    },
                  },
                  arrow: { sx: { color: "var(--panel-bg)" } },
                }}
              >
                <span tabIndex={0} className="inline-flex cursor-help items-center text-[var(--ink-soft)] underline decoration-[var(--ink-soft)] decoration-dotted underline-offset-4 outline-none transition-colors hover:text-[var(--ink)] focus-visible:text-[var(--ink)]">
                  {(docConf.signals || []).length} signals
                </span>
              </Tooltip>
            </div>
          );
        })()
      ) : (
        <span className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-[var(--ink-soft)]">
          {hasReport ? "Scan report received" : "Report data unavailable"}
        </span>
      )}

      {(isReadinessView || view === "citations" || view === "citations-loading") && totalRecCount > 0 && (
        <button onClick={() => { trackEvent("recommendations_viewed"); setView("recommendations"); }} className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)] px-3 py-1.5 text-[var(--ink)] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]">
          View all {totalRecCount} recommendations <ArrowRight className="size-3" strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}

      {hasReport && (
        <DownloadReportButton
          onDownload={(format) => { void reportDownload.download(format); }}
          onSkipCitations={reportDownload.skipCitations}
          pending={reportDownload.pending}
          waitingSince={reportDownload.waitingSince}
          building={reportDownload.building}
          error={reportDownload.error}
        />
      )}

      {view === "recommendations" && (
        <button onClick={backToReadiness} className="link-sweep inline-flex items-center gap-2 text-[var(--ink-soft)] hover:text-[var(--accent)]">
          <ArrowLeft className="size-3" strokeWidth={1.8} aria-hidden="true" />Back to readiness
        </button>
      )}
    </div>

    {isReadinessView && <>{v2 ? <div data-testid="reachability-banner" className="mt-6 flex flex-col gap-2 rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-[11px] font-medium tracking-[-.025em] text-[var(--ink)]">{v2 && gateBannerText(v2, botAccessState === "js_blocked")}</span>
        <a href={robotsUrl} target="_blank" rel="noopener noreferrer" className="ml-auto text-[11px] font-medium tracking-[-.025em] text-[var(--ink)] transition-colors" style={{ textDecoration: "underline", textUnderlineOffset: "4px" }}>robots.txt</a>
      </div>
      {v2?.gate.mismatch && <span className="text-[11px] font-medium tracking-[-.025em] text-[var(--ink)]">Your robots.txt allows AI bots, but the server refused an agent that named itself. Check your bot protection (for example Cloudflare) and allow verified AI agents.</span>}
      {botAccessState === "js_blocked" && <span className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">This page is a JavaScript-rendered SPA. Most AI bots do not execute JavaScript, making your content invisible to them regardless of your robots.txt configuration. Lensy rendered this page for content analysis; most AI bots cannot.</span>}
      <div className="mt-1 flex flex-wrap gap-1.5">{v2?.bots.map((bot) => <span key={bot.name} aria-label={`${bot.name}: ${bot.allowed ? "allowed" : "blocked"} by robots.txt`} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--line-soft)] bg-[var(--surface-2)] px-2.5 py-1 text-[10px] font-medium leading-none tracking-[-.02em] text-[var(--ink-soft)]">{bot.allowed ? <CheckCircle2 className="size-3 shrink-0" style={{ color: "var(--accent)" }} strokeWidth={2} aria-hidden="true" /> : <XCircle className="size-3 shrink-0" style={{ color: "var(--ink-soft)" }} strokeWidth={2} aria-hidden="true" />}<span aria-hidden="true">{bot.name}</span></span>)}</div>
    </div> : <div className="mt-6 flex flex-col gap-2 rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] px-4 py-3"><div className="flex flex-wrap items-center gap-x-3 gap-y-1"><span className="text-[11px] font-medium tracking-[-.025em] text-[var(--ink)]">{botAccessState === 'js_blocked' ? "! AI bot access is blocked by JavaScript" : botAccessBannerText()}</span>{botAccess?.robotsTxtFound ? <a href={robotsUrl} target="_blank" rel="noopener noreferrer" className="ml-auto text-[11px] font-medium tracking-[-.025em] text-[var(--ink)] transition-colors" style={{ textDecoration: "underline", textUnderlineOffset: "4px" }}>robots.txt</a> : <span className="ml-auto text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">No robots.txt found</span>}</div>{(botAccessState === 'js_blocked' || !botAccess?.bots?.length) && <span className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">{botAccessState === 'js_blocked' ? "This page is a JavaScript-rendered SPA. Most AI bots do not execute JavaScript, making your content invisible to them regardless of your robots.txt configuration. Lensy rendered this page for content analysis; most AI bots cannot." : hasReport ? "No checked crawler names were returned." : "No bot access data returned."}</span>}{botAccess?.bots?.length > 0 && <div className="mt-1 flex flex-wrap gap-1.5">{botAccess.bots.map((bot: any, i: number) => { const blocked = botAccessState === 'js_blocked' || !(bot.status === 'allowed' || bot.status === 'not-mentioned'); /* T-07: the icon alone carried the allowed/blocked state and was aria-hidden, so screen readers only heard the bot name. */ return <span key={i} aria-label={`${bot.name}: ${blocked ? "blocked" : "allowed"}`} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--line-soft)] bg-[var(--surface-2)] px-2.5 py-1 text-[10px] font-medium leading-none tracking-[-.02em] text-[var(--ink-soft)]">{blocked ? <XCircle className="size-3 shrink-0" style={{ color: "var(--ink-soft)" }} strokeWidth={2} aria-hidden="true" /> : <CheckCircle2 className="size-3 shrink-0" style={{ color: "var(--accent)" }} strokeWidth={2} aria-hidden="true" />}<span aria-hidden="true">{bot.name}</span></span>; })}</div>}</div>}<div data-testid="signal-groups" className={`mt-5 grid items-start gap-3 ${shownGroups.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}>{shownGroups.map((group) => <section key={group.title} className="rounded-[var(--radius-md)] border border-[var(--line)] bg-[var(--surface)] p-6"><div className="flex items-center justify-between border-b border-[var(--line)] pb-4"><h2 className="text-[11px] font-medium tracking-[-.025em] text-[var(--ink-soft)]">{group.title}</h2><SignalStatusIcon status={groupStatus(group)} className="size-4 shrink-0" /></div>{group.sections.map((section: any) => <div key={section.title || group.title} className="mt-5 first:mt-5"><p className={`text-[11px] font-medium uppercase tracking-[.08em] text-[var(--muted)] ${section.title ? "mb-4" : "sr-only"}`}>{section.title || "Signals"}</p><ul className="space-y-5">{section.signals.map((sig: Signal) => <li key={sig.label} className="flex gap-2.5"><SignalStatusIcon status={sig.status} className="mt-0.5 size-4 shrink-0" /><div className="min-w-0"><div className="flex flex-wrap items-center gap-x-3 gap-y-2"><p className="text-[14px] tracking-[-.025em]">{sig.label}</p><EvidenceBadge signal={sig.signal} /><AudienceBadge audience={sig.signal?.audience || sig.audience} />{sig.points ? <span data-testid="signal-points" className="inline-flex items-center rounded-full border border-[var(--line-soft)] bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-medium tabular-nums leading-none tracking-[-.02em] text-[var(--ink-soft)]">{sig.points}</span> : sig.status !== "pass" && <ImpactBadge impact={resolveImpact(sig.label)} />}<InfoHint text={sig.info} /></div><p className="mt-1 text-[12px] leading-relaxed text-[var(--ink-soft)]">{sig.detail}</p>{sig.status !== "pass" && (() => { const fix = sig.fix ?? fixForSignal(sig.label); return fix ? <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--ink)]"><span className="font-medium">Fix: </span>{fix}</p> : null; })()}{sig.signal?.note && <p className="mt-1 text-[11px] italic leading-relaxed" style={{ color: "var(--ink-soft)" }}>{sig.signal.note}</p>}{sig.waitlist && <a href={`/contact?ref=${sig.waitlist}`} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("generate_markdown_link_clicked", { ref: sig.waitlist })} className="group mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium tracking-[-.02em] text-[var(--ink)]"><span style={{ textDecoration: "underline", textUnderlineOffset: "3px" }}>{sig.waitlist === "llmstxt" ? "We can help you generate one" : "We can help you generate markdown"}</span><ArrowRight className="arrow-nudge size-3" strokeWidth={1.8} aria-hidden="true" /></a>}</div></li>)}</ul></div>)}{group.key === "coding" && v2?.preview && (() => { const pv = v2.preview; const share = Math.min(1, pv.windowShare); return <div data-testid="agent-preview" className="mt-6 border-t border-[var(--line)] pt-5"><p className="text-[11px] font-medium uppercase tracking-[.08em] text-[var(--muted)]">What an agent receives</p><p className="mt-3 text-[13px] leading-relaxed text-[var(--ink)]">{previewSummary(pv)}.</p><div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]" role="img" aria-label={`${Math.round(share * 100)}% of the page fits in one 100,000-character fetch`}><div className="h-full rounded-full bg-[var(--ink)]" style={{ width: `${Math.max(2, Math.round(share * 100))}%` }} /></div><p className="mt-2 text-[11px] leading-relaxed text-[var(--ink-soft)]">Agents read about 100,000 characters per fetch.{pv.url ? <> Fetched from <span className="break-all">{pv.url}</span>{pv.format === "markdown" ? " as Markdown." : "."}</> : null}</p></div>; })()}</section>)}</div>{v2 && v2.tips.length > 0 && <div data-testid="report-tips" className="mt-5 rounded-[var(--radius-md)] border border-dashed border-[var(--line)] bg-[var(--surface)] px-5 py-4"><p className="text-[11px] font-medium uppercase tracking-[.08em] text-[var(--muted)]">Also worth knowing (not scored)</p><ul className="mt-3 space-y-2">{v2.tips.map((tip) => <li key={tip} className="text-[12px] leading-relaxed text-[var(--ink-soft)]">{tip}</li>)}</ul></div>}</>}

    {CITATIONS_ENABLED && view === "citations-loading" && <div className="mt-10"><p className="text-[15px] leading-relaxed text-[var(--ink-soft)]">AI-generated queries are being tested against configured AI search engines.</p><p className="mt-10 text-center text-[12px] font-medium tracking-[-.025em] text-[var(--accent)]">Testing citation queries…</p><div className="mt-6 grid gap-3">{Array.from({ length: 4 }).map((_, index) => <div key={index} className="grid grid-cols-[1.5fr_.65fr] gap-5 border-t border-[var(--line)] py-4"><span className="h-3 animate-pulse bg-[var(--surface-2)]" /><span className="h-3 animate-pulse bg-[var(--surface-2)]" /></div>)}</div></div>}

    {CITATIONS_ENABLED && view === "citations" && <div className="mt-10"><p className="text-[15px] leading-relaxed text-[var(--ink-soft)]">{citationsLoading ? "AI search is checking the generated queries. Results will appear here when the scan controller receives them." : aiDisc?.status === "error" ? "The AI citation check could not run this time. Click the AI citations card to try again." : citationResults.length ? "Citation results returned by the completed AI search check." : citationsReturned && availableCitationEngines.length === 0 ? "The citation check completed, but no configured AI search engine was available to test this documentation." : citationsReturned ? "The citation check completed but did not return query-level results." : "Start the citation check to test whether AI search can find and cite your documentation."}</p>{citationResults.length > 0 && <><div className="mt-6 inline-flex rounded-full bg-[var(--surface-2)] px-3 py-1 text-[11px] font-medium tracking-[-.025em] text-[var(--ink-soft)]">Cited: {citedCount} / {citationResults.length} tested queries</div>{renderCitationTable()}</>}{citationsReturned && citationResults.length === 0 && aiDisc?.recommendations?.length > 0 && <div className="mt-6 divide-y divide-[var(--line)] border-y border-[var(--line)]">{aiDisc.recommendations.map((recommendation: any) => <div key={recommendation.issue} className="py-4"><p className="text-[14px] tracking-[-.025em]">{recommendation.issue}</p><p className="mt-1 text-[12px] leading-relaxed text-[var(--ink-soft)]">{recommendation.fix}</p></div>)}</div>}</div>}

    {view === "recommendations" && <div className="mt-10">
      <h2 className="text-[19px] font-medium tracking-[-.04em]">All recommendations <span className="text-[var(--muted)]">({totalRecCount})</span></h2>
      <p className="mt-2 text-[13px] leading-relaxed text-[var(--ink-soft)]" style={{ maxWidth: "none" }}>Quick Wins and Deeper Improvements affect your score. Things to Watch are informational and don't impact scoring.</p>

      {totalRecCount === 0 && <p className="mt-8 text-[14px] text-[var(--ink-soft)]">No recommendations — this page is well-optimized for AI tools.</p>}

      {quickWins.length > 0 && <div className="mt-12">
        <div className="flex items-baseline gap-2">
          <h3 className="text-[11px] font-medium uppercase tracking-[.09em] text-[var(--ink)]">Quick Wins</h3>
          <span className="text-[11px] font-medium tabular-nums text-[var(--muted)]">{quickWins.length}</span>
        </div>
        <div className="mt-5 grid gap-3">{quickWins.map((rec, i) => renderRec(rec, `qw-${i}`))}</div>
      </div>}

      {deeperImprovements.length > 0 && <div className="mt-12">
        <div className="flex items-baseline gap-2">
          <h3 className="text-[11px] font-medium uppercase tracking-[.09em] text-[var(--ink)]">Deeper Improvements</h3>
          <span className="text-[11px] font-medium tabular-nums text-[var(--muted)]">{deeperImprovements.length}</span>
        </div>
        <div className="mt-5 grid gap-3">{deeperImprovements.map((rec, i) => renderRec(rec, `di-${i}`))}</div>
      </div>}

      {thingsToWatch.length > 0 && <div className="mt-12">
        <div className="flex items-baseline gap-2">
          <h3 className="text-[11px] font-medium uppercase tracking-[.09em] text-[var(--muted)]">Things to Watch</h3>
          <span className="text-[11px] font-medium tabular-nums text-[var(--muted)]">{thingsToWatch.length}</span>
        </div>
        <p className="mt-1.5 text-[11px] text-[var(--muted)]">Emerging patterns that don't affect your score</p>
        <div className="mt-5 grid gap-3">{thingsToWatch.map((rec, i) => <article key={`ttw-${i}`} className="rounded-[var(--radius-md)] border border-dashed border-[var(--line)] bg-[var(--surface)] p-5"><p className="text-[15px] font-medium leading-snug tracking-[-.03em] text-[var(--ink)]">{recIssue(rec)}</p>{rec.category && <div className="mt-3"><span className={chipClass}>{rec.category}</span></div>}{recFix(rec) && <p className="mt-3 text-[13px] leading-relaxed text-[var(--ink-soft)]">{recFix(rec)}</p>}</article>)}</div>
      </div>}
    </div>}

    {/* Shared footer note — shown on all three views (readiness, recommendations, citations),
        carrying the standing scan note and a Share feedback link. */}
    {(isReadinessView || view === "recommendations" || view === "citations" || view === "citations-loading") && (
      <div className="mt-12 border-l-2 border-[var(--accent)] bg-[var(--surface)] px-5 py-4 text-[12px] leading-relaxed text-[var(--ink-soft)]">
        Results above are generated from the completed Lensy scan.{CITATIONS_ENABLED ? " Citation results appear only after the citation check returns data." : ""} <a href="/contact?ref=feedback" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("contact_link_clicked", { source: "results-share-feedback" })} className="font-medium text-[var(--ink)]" style={{ textDecoration: "underline", textUnderlineOffset: "3px" }}>Share feedback</a>
      </div>
    )}
  </section>;
}

export function HowItWorks() {
  const steps = [
    ["Paste a URL", "Enter any docs page. API references, guides, or tutorials."],
    ["Lensy scans the page", "We check what AI search and coding agents can reach and read: bot access, Markdown, llms.txt, headings and freshness, in real time."],
    ["Get your scores", "See two scores out of 100, one for AI search and one for coding agents, with exact steps to fix any issues."],
    ...(CITATIONS_ENABLED ? [["Check citations", "Find out if AI search engines like Perplexity actually cite your page right now."]] : []),
  ];
  const flow = [
    ["01", "Paste Documentation URL", "docs.example.com/api"],
    ["02", "Content Validation", "Is this technical documentation?"],
    ...(CITATIONS_ENABLED ? [["04", "AI Citation Check", "Perplexity AI Search"], ["05", "Scores + Recommendations", ""]] : [["04", "Scores + Recommendations", ""]]),
  ];

  return <>
    <header className="mx-auto grid max-w-[1240px] gap-8 border-b border-[var(--line)] px-5 pb-14 pt-16 sm:px-8 sm:pb-20 sm:pt-24 lg:grid-cols-12">
      <h1 style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }} className="hero-intro max-w-3xl font-medium leading-[.88] tracking-[-.075em] lg:col-span-8">See exactly how bots <span className="text-[var(--accent)]">read your docs.</span></h1>
      <p className="self-end text-[15px] leading-relaxed text-[var(--ink-soft)] lg:col-span-4">{CITATIONS_ENABLED ? "Give Lensy a URL, and it shows you exactly how AI search engines read and cite your page." : "Give Lensy a URL, and it shows you exactly how AI search engines and coding agents read your page."}</p>
    </header>
    <section data-reveal className="mx-auto grid max-w-[1240px] gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-5"><p className="text-[12px] font-medium tracking-[-.025em] text-[var(--muted)]">The process</p><ol className="mt-7 border-t border-[var(--line)]">{steps.map(([title, description], index) => <li key={title} className="grid grid-cols-[32px_minmax(0,1fr)] gap-4 border-b border-[var(--line)] py-6"><span className="text-[11px] font-medium tracking-[-.025em] text-[var(--accent)]">0{index + 1}</span><div><h2 className="text-[19px] font-light tracking-[-.04em]">{title}</h2><p className="mt-2 max-w-md text-[13px] leading-relaxed text-[var(--ink-soft)]">{description}</p></div></li>)}</ol><Link to="/#scan" className="btn-ink group mt-8 inline-flex items-center gap-2 bg-[var(--panel-bg)] px-4 py-3 text-[12px] font-medium tracking-[-.025em] text-[var(--panel-fg)]">Run a free scan <ArrowRight className="arrow-nudge size-3" strokeWidth={1.8} aria-hidden="true" /></Link></div>
      <div className="lg:col-span-6 lg:col-start-7">
        <div className="flex items-baseline justify-between border-b border-[var(--line)] pb-4">
          <p className="text-[12px] font-medium tracking-[-.025em] text-[var(--muted)]">From page to report</p>
          <p className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">{flow.length + 1} stages</p>
        </div>
        <div className="mt-7 rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] p-5 sm:p-8">
          <div className="mx-auto max-w-[420px]">
            {flow.slice(0, 2).map(([number, title, detail]) => <div key={title}>
              <FlowNode number={number} title={title} detail={detail} />
              <FlowConnector />
            </div>)}
            <div className="rounded-[var(--radius-md)] border border-[var(--ink)] bg-[var(--bg)] p-5">
              <div className="flex items-start justify-between gap-6"><div><p className="text-[11px] font-medium tracking-[-.025em] text-[var(--accent)]">03</p><h2 className="mt-3 text-[19px] font-light tracking-[-.045em]">AI Readiness Analysis</h2></div><span className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">parallel analysis</span></div>
              <div className="mt-5 grid grid-cols-3 border-y border-[var(--line)]">
                {["AI search", "Coding agents", "Reachability"].map((signal, index) => <div key={signal} className={`py-3 text-center text-[10px] font-medium leading-snug tracking-[-.02em] text-[var(--ink-soft)] ${index ? "border-l border-[var(--line)]" : ""}`}>{signal}</div>)}
              </div>
              <p className="mt-4 text-center text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">+ checks both audiences need, such as content without JavaScript</p>
            </div>
            <FlowConnector />
            {flow.slice(2).map(([number, title, detail], index) => <div key={title}>
              <FlowNode number={number} title={title} detail={detail} final={index === flow.length - 3} />
              {index < flow.length - 3 && <FlowConnector />}
            </div>)}
          </div>
        </div>
      </div>
    </section>
  </>;
}

export function FlowConnector() {
  return <div className="flex h-9 flex-col items-center justify-center" aria-hidden="true"><span className="h-4 border-l border-[var(--line)]" /><ArrowDown className="size-3 text-[var(--accent)]" strokeWidth={1.8} /></div>;
}

export function FlowNode({ number, title, detail, final = false }: { number: string; title: string; detail: string; final?: boolean }) {
  return <div className={`rounded-[var(--radius-md)] border px-5 py-4 ${final ? "border-[var(--ink)] bg-[var(--panel-bg)] text-[var(--panel-fg)]" : "border-[var(--line)] bg-[var(--bg)]"}`}><div className="flex items-start gap-4"><span className={`mt-0.5 text-[11px] font-medium tracking-[-.025em] ${final ? "opacity-60" : "text-[var(--accent)]"}`}>{number}</span><div><h2 className="text-[16px] font-light tracking-[-.04em]">{title}</h2>{detail && <p className="mt-1 text-[12px] leading-relaxed opacity-70">{detail}</p>}</div></div></div>;
}

// ---- Education ----

type ArticleData = {
  slug: string;
  title: string;
  tag: string;
  readTime: string;
  description: string;
  // N-11: the real date each article was first published, so the generated
  // .md/sitemap.xml lastmod stops being stamped to today's build date.
  publishedDate: string;
  body: React.ReactNode;
};

const ARTICLES: ArticleData[] = [
  {
    slug: "what-changed-in-lensy-after-rechecking-ai-ready-docs-signals",
    title: "What Changed in Lensy After Re-checking AI-Ready Docs Signals",
    publishedDate: "2026-09-16",
    tag: "AI Readiness",
    readTime: "4 min read",
    description: "Some documentation sites already expose machine-readable content in ways a simpler audit can miss. Why we re-checked Lensy's detection model — and what changed.",
    body: <>
      <Prose>
        <TlDr items={[
          "Markdown discoverability is no longer just a .md file check. The current ecosystem now includes same-URL Markdown negotiation through the Accept header, llms.txt, llms-full.txt, and page-level discovery headers [1, 2, 3].",
          "The llms.txt proposal allows /llms.txt at the root or in a subpath, which means a docs audit can produce false negatives if it only checks one location [3].",
          "WebSRC showed that machine understanding of web pages depends on structure, not just text, which is why AI-readiness detection has to account for how documentation is actually exposed to machines [4].",
        ]} />

        <h2>Why We Revisited This</h2>
        <p>Lensy's detection model was re-checked after new feedback exposed an important pattern: some documentation sites already expose machine-readable content in ways a simpler audit can miss.</p>
        <p>That matters because an AI-readiness audit is not just asking whether a page exists. It is asking whether AI systems can find it, fetch it in a machine-friendly format, and map it back to the broader docs structure.</p>
        <p>The original llms.txt proposal focused on a plain Markdown index and linked Markdown pages [3]. Since then, the implementation surface has expanded. Docs platforms now expose full-site context files, same-URL Markdown negotiation, and per-page headers that point agents to the right index or representation [1, 2].</p>

        <h2>Markdown Is Broader Than .md</h2>
        <p>The original proposal recommends serving Markdown pages with <code>.md</code> appended to the original URL [3]. That remains a useful convention. But it is no longer the only one that matters.</p>
        <p>Cloudflare's Markdown for Agents serves Markdown from the same URL when a client sends an Accept request for Markdown [2]. That can materially change how much useful context an agent can consume: Cloudflare shows one page dropping from 16,180 tokens in HTML to 3,150 tokens in Markdown, roughly an 80% reduction [2].</p>
        <p>That means a docs audit cannot stop at probing <code>page.md</code>. It also needs to test whether the page itself returns Markdown through content negotiation.</p>

        <h2>llms.txt Discovery Is Broader Than One Path</h2>
        <p>The proposal explicitly allows llms.txt in the root or in a subpath [3]. That matters because many docs live under <code>/docs/</code>, <code>/developer/</code>, or another scoped path.</p>
        <p>Current implementations also go beyond llms.txt alone. Mintlify documents llms-full.txt, Link and X-Llms-Txt headers, and .md page links inside llms.txt itself [1]. Those change what a high-quality docs audit should verify.</p>
        <p>In practice, that means checking more than whether <code>/llms.txt</code> exists. It also means checking whether the site exposes llms-full.txt [1], the page advertises docs indexes through headers [1], the page returns Markdown directly through an Accept request [1, 2], and the exact page is mapped inside llms.txt as a Markdown URL [1].</p>

        <h2>What Changed in Lensy</h2>
        <p>Lensy's current audit model now reflects that broader pattern. The AI Readiness view reports discrete signals rather than collapsing everything into a single grade, and it separates site-level and page-level checks such as llms.txt, llms-full.txt, page Markdown, content negotiation, and page-level mapping in llms.txt.</p>
        <p>It also treats some signals more carefully than before. A Link header is treated as a hint that still needs validation, not proof. A best-practice signal like <code>rel="alternate"</code> for a Markdown version is still worth surfacing, but it is no longer treated as the strongest evidence of machine-readable support when llms.txt, direct Markdown URLs, or content negotiation already exist [1, 2, 3].</p>

        <h2>What This Means</h2>
        <p>The broader lesson is simple. AI-ready documentation is no longer just about whether a page is crawlable or well written. It is also about whether the site exposes machine-readable paths in the ways modern agents actually use them.</p>
        <p>Research still supports the underlying principle. WebSRC found that answering questions about web pages requires understanding page structure, not just the text on the page [4]. The more documentation platforms expose agent-friendly structure directly, the more accurate an AI-readiness audit also has to become.</p>

        <References items={[
          { n: 1, text: 'Mintlify. "llms.txt." Documentation, 2025.', href: "https://www.mintlify.com/docs/ai/llmstxt" },
          { n: 2, text: 'Cloudflare. "Introducing Markdown for Agents." Cloudflare Blog, 2026.', href: "https://blog.cloudflare.com/markdown-for-agents/" },
          { n: 3, text: 'Howard, Jeremy. "The /llms.txt file." llmstxt.org, 2024.', href: "https://llmstxt.org" },
          { n: 4, text: 'Chen et al. "WebSRC: A Dataset for Web-Based Structural Reading Comprehension." EMNLP, 2021.', href: "https://aclanthology.org/2021.emnlp-main.343/" },
        ]} />
      </Prose>
    </>,
  },
  {
    slug: "research-behind-ai-ready-docs",
    title: "The Research Behind AI-Ready Documentation",
    publishedDate: "2026-03-22",
    tag: "AI Readiness",
    readTime: "3 min read",
    description: "The research behind the four things Lensy measures: bot access, content structure, structured data, and discoverability.",
    body: <>
      <Prose>
        <TlDr items={[
          "Document structure directly affects AI retrieval quality. DeepRead showed 10.3% improvement and RAPTOR showed 20% improvement with structure-aware processing.",
          "ClaudeBot is blocked by 69% of websites and GPTBot by 62%. 71% of sites that block training bots also block search crawlers, removing themselves from AI results entirely.",
          "Structured data helps search engines understand a page, but controlled studies find JSON-LD barely changes AI citations. Heading hierarchies and canonical URLs matter more.",
        ]} />

        <h2>Why Document Structure Matters</h2>
        <p>Most AI search engines use Retrieval-Augmented Generation (RAG). They break documents into chunks, embed them in vector space, retrieve relevant chunks for a query, and feed those chunks to a language model. The quality of that chunking, and the structural signals available to guide it, directly affects answer quality.</p>
        <p>DeepRead [1] found that preserving heading hierarchy during HTML-to-Markdown conversion enables a "locate-then-read" paradigm, improving retrieval by 10.3% over baseline agentic search. RAPTOR [2] showed 20% improvement on multi-step reasoning by building recursive tree structures from document hierarchies. The DUE benchmark [3] further established that document understanding requires explicit layout awareness across tasks spanning VQA, key information extraction, and machine reading comprehension.</p>
        <p>Snowflake's engineering team [4] confirmed that Markdown-aware chunking provides 5–10% accuracy improvement in RAG quality for complex documents, and that retrieval strategy matters even with long-context LLMs.</p>

        <h2>Bot Access: The Crawler Gate</h2>
        <p>Major AI companies operate multiple crawlers with distinct purposes. Anthropic runs ClaudeBot (training), Claude-User (real-time fetches), and Claude-SearchBot (indexing). OpenAI separates GPTBot (training) from OAI-SearchBot (search results). Google separates Googlebot (search ranking) from Google-Extended (Gemini training) [7].</p>
        <p>Industry analysis shows ClaudeBot is blocked by approximately 69% of websites and GPTBot by 62% [5]. More critically, 71% of sites that block training bots also inadvertently block search and retrieval bots [6], effectively removing themselves from AI-powered search results. OpenAI has stated that sites blocking OAI-SearchBot will not appear in ChatGPT search answers.</p>

        <h2>Structured Data</h2>
        <p>JSON-LD uses the Schema.org vocabulary to declare page types (<code>TechArticle</code>, <code>APIReference</code>), authors, publication dates, and topic relationships. Controlled data does not show it raising AI citations: Ahrefs [8] tracked 1,885 pages that added schema against 4,000 controls (May 2026), and their AI citations barely moved. That is why Lensy gives JSON-LD only 5 of the 100 AI search points. Google's Search Central documentation [9] confirms that structured data directly affects how content appears in search results and AI overviews.</p>
        <p>OpenGraph meta tags influence how AI-powered preview systems and content aggregation tools summarise your content. Canonical URLs consolidate link equity and prevent AI engines from indexing duplicate versions [10], which is especially important for documentation sites serving versioned or localised content.</p>

        <h2>What This Means</h2>
        <p>84% of developers now use or plan to use AI tools [11]. Documentation that meets structural, access, and metadata standards gets cited. Documentation that does not is invisible to this growing majority.</p>

        <References items={[
          { n: 1, text: 'Li et al. "DeepRead: Document Structure-Aware Reasoning." arXiv:2602.05014, 2026.', href: "https://arxiv.org/abs/2602.05014" },
          { n: 2, text: 'Sarthi et al. "RAPTOR: Recursive Abstractive Processing for Tree-Organized Retrieval." ICLR 2024.', href: "https://arxiv.org/abs/2401.18059" },
          { n: 3, text: 'Borchmann et al. "DUE: End-to-End Document Understanding Benchmark." NeurIPS 2021.', href: "https://datasets-benchmarks-proceedings.neurips.cc/paper/2021/hash/069059b7ef840f0c74a814ec9237b6ec-Abstract-round2.html" },
          { n: 4, text: 'Snowflake. "How Retrieval & Chunking Impact Finance RAG." 2024.', href: "https://www.snowflake.com/en/engineering-blog/impact-retrieval-chunking-finance-rag/" },
          { n: 5, text: 'ALM Corp. "ClaudeBot, Claude-User & Claude-SearchBot: Anthropic\'s Three-Bot Framework." 2025.', href: "https://almcorp.com/blog/anthropic-claude-bots-robots-txt-strategy/" },
          { n: 6, text: 'Search Engine Journal. "Anthropic\'s Claude Bots Make Robots.txt Decisions More Granular." 2025.', href: "https://www.searchenginejournal.com/anthropics-claude-bots-make-robots-txt-decisions-more-granular/568253/" },
          { n: 7, text: "Google. \"Google's Common Crawlers.\" Google for Developers, 2025.", href: "https://developers.google.com/crawling/docs/crawlers-fetchers/google-common-crawlers" },
          { n: 8, text: 'Ahrefs. "We Tracked 1,885 Pages Adding Schema." 2026.', href: "https://ahrefs.com/blog/schema-ai-citations/" },
          { n: 9, text: 'Google. "Introduction to Structured Data." Search Central.', href: "https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data" },
          { n: 10, text: 'Google. "Canonicalisation." Search Central.', href: "https://developers.google.com/search/docs/crawling-indexing/canonicalization" },
          { n: 11, text: 'Stack Overflow. "2025 Developer Survey Results." 2025.', href: "https://survey.stackoverflow.co/2025/" },
        ]} />
      </Prose>
    </>,
  },
  {
    slug: "how-ai-search-finds-and-cites-docs",
    title: "How AI Search Finds, Processes, and Cites Your Docs",
    publishedDate: "2026-03-22",
    tag: "AI Discoverability",
    readTime: "3 min read",
    description: "Inside the RAG pipeline: crawling, chunking, retrieval, and citation. What llms.txt changes, and how platforms like Perplexity and ChatGPT decide what to cite.",
    body: <>
      <Prose>
        <TlDr items={[
          "Chunking strategy matters. Element-based chunking that respects document structure achieved 84.4% page-level accuracy, while 512-token windows with 200-token overlap scored highest across 90 configurations.",
          "llms.txt lets you serve Markdown directly to AI agents. Fern reports over 90% reduction in token consumption. Twilio, Stripe, and Cloudflare have also adopted it.",
          "Perplexity cites sources on nearly every response. Brands mentioned positively across 4+ platforms are 2.8× more likely to appear in ChatGPT responses.",
        ]} />

        <h2>How Chunking Works</h2>
        <p>AI search engines crawl your page, convert HTML to text or Markdown, split it into chunks, embed those chunks in vector space, and retrieve relevant pieces to generate an answer. Each stage is sensitive to document quality.</p>
        <p>Jimeno-Yepes et al. [1] demonstrated that element-based chunking that respects document structure — headings, paragraphs, code blocks — rather than splitting at arbitrary character boundaries achieved 84.4% accuracy at page level and improved ROUGE and BLEU scores over naive splitting. Stäbler and Turnbull [2] benchmarked 90 chunker-model configurations across 7 domains and found sentence-based splitting with 512-token windows and 200-token overlap achieved the highest retrieval accuracy. Vectara's study [3] tested 25 chunking configurations across 48 embedding models and confirmed that fixed-size chunking at 256–512 tokens consistently outperformed more computationally expensive semantic chunking.</p>

        <h2>llms.txt and Markdown Alternate Links</h2>
        <p>Proposed by Jeremy Howard of Answer.AI in September 2024, <code>llms.txt</code> [4] is a Markdown file that helps LLMs navigate websites by providing structured links to content instead of requiring models to parse HTML boilerplate. Adoption has been rapid:</p>
        <ul>
          <li><strong>Twilio</strong> [5] exposes Markdown versions of all docs (append <code>.md</code> to any URL) plus a curated <code>llms.txt</code> sitemap.</li>
          <li><strong>Fern</strong> [6] serves two variants: lightweight <code>/llms.txt</code> with summaries and comprehensive <code>/llms-full.txt</code>. Markdown serving reduces token consumption by over 90%.</li>
          <li><strong>Stripe's</strong> <code>llms.txt</code> includes an "instructions" section that guides AI to the right integration path.</li>
          <li><strong>Cloudflare</strong> organised theirs by service category for selective retrieval.</li>
        </ul>
        <p>Beyond <code>llms.txt</code>, individual pages can signal Markdown availability using <code>{"<link rel=\"alternate\" type=\"text/markdown\" href=\"/path/to/page.md\">"}</code> in the HTML head. This per-page approach complements <code>llms.txt</code> by letting AI agents discover the Markdown version of any specific page they land on, without needing to consult a central index first.</p>

        <h2>How Platforms Decide What to Cite</h2>
        <p>Citation behaviour varies significantly across platforms. Perplexity is retrieval-first and cites sources on nearly every response, with Reddit as its most-cited source at 6.6% of all citations [7]. ChatGPT cites when browsing is enabled, with Wikipedia leading at 7.8% [7]. Google AI Overviews distributes citations more evenly across sources.</p>
        <p>Several patterns emerge. Pages with clear headings, code examples, and step-by-step instructions get cited more than dense prose. Technical reference queries trigger citations more reliably than generic how-tos. Brands mentioned positively across 4+ non-affiliated platforms are 2.8× more likely to appear in ChatGPT responses [8].</p>

        <References items={[
          { n: 1, text: 'Jimeno-Yepes et al. "Financial Report Chunking for Effective RAG." arXiv:2402.05131, 2024.', href: "https://arxiv.org/abs/2402.05131" },
          { n: 2, text: 'Stäbler, Turnbull et al. "Chunking Strategies for Domain-Specific IR in RAG." IEEE, 2024.', href: "https://ieeexplore.ieee.org/document/11125724" },
          { n: 3, text: 'Qu et al. "Is Semantic Chunking Worth the Computational Cost?" NAACL 2025.', href: "https://arxiv.org/abs/2410.13070" },
          { n: 4, text: 'Howard, J. "llms.txt: A Proposal to Help LLMs Use Websites." Answer.AI, 2024.', href: "https://www.answer.ai/posts/2024-09-03-llmstxt.html" },
          { n: 5, text: 'Twilio. "Docs Support for llms.txt and Markdown." 2024.', href: "https://www.twilio.com/en-us/blog/developers/docs-llms-txt-markdown-support" },
          { n: 6, text: 'Fern. "Markdown for LLMs." 2025.', href: "https://buildwithfern.com/learn/docs/ai-features/llms-txt" },
          { n: 7, text: 'Yext. "How AI Engines Decide What to Cite." 2026.', href: "https://www.yext.com/blog/2026/03/how-chatgpt-perplexity-gemini-claude-decide-what-to-cite" },
          { n: 8, text: 'XFunnel. "What Sources Do AI Search Engines Cite?" 2026.', href: "https://www.xfunnel.ai/blog/what-sources-do-ai-search-engines-choose" },
        ]} />
      </Prose>
    </>,
  },
];

export function TlDr({ items }: { items: string[] }) {
  return <div className="not-prose mb-10 border-l-2 border-[var(--accent)] bg-[var(--surface)] px-5 py-5">
    <p className="text-[11px] font-medium tracking-[-.025em] text-[var(--accent)] mb-3">TL;DR</p>
    <ul className="flex flex-col gap-2">
      {items.map((item, i) => <li key={i} className="flex gap-3 text-[13px] leading-relaxed text-[var(--ink-soft)]"><span className="mt-0.5 shrink-0 text-[11px] font-medium tracking-[-.025em] text-[var(--accent)]">·</span>{item}</li>)}
    </ul>
  </div>;
}

export function References({ items }: { items: { n: number; text: string; href: string }[] }) {
  return <div className="not-prose mt-12 border-t border-[var(--line)] pt-8">
    <p className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)] mb-4">References</p>
    <ol className="flex flex-col gap-2">
      {items.map(({ n, text, href }) => <li key={n} className="flex gap-3 text-[12px] leading-relaxed text-[var(--ink-soft)]">
        <span className="shrink-0 text-[11px] font-medium tracking-[-.025em] text-[var(--accent)]">[{n}]</span>
        <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent)] underline underline-offset-2 decoration-[var(--line-strong)]">{text}</a>
      </li>)}
    </ol>
  </div>;
}

export function Prose({ children }: { children: React.ReactNode }) {
  return <div className="prose-lensy">{children}</div>;
}

export function Education() {
  return <Page
    titleStyle={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
    title={<>Education<br />on <span className="text-[var(--accent)]">AI-ready</span> docs.</>}
    intro="What we are learning about making docs readable for AI search."
  >
    <section className="mt-16 pb-20 sm:mt-20 sm:pb-28">
      <div className="flex items-end justify-between border-b border-[var(--line)] pb-4 text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">
        <span>Reading list</span>
        <span>{ARTICLES.length} articles</span>
      </div>
      <div>
        {ARTICLES.map((article, i) => (
          <article key={article.slug} data-reveal style={{ "--reveal-delay": `${i * 70}ms` } as React.CSSProperties} className="group border-b border-[var(--line)]">
            <Link to={`/education/${article.slug}`} className="soft-card grid gap-4 py-8 transition-colors hover:bg-[var(--surface-2)] rounded-[var(--radius-md)] sm:grid-cols-[42px_minmax(0,1fr)_auto] sm:gap-6 sm:-mx-5 sm:px-5">
              <p className="text-[11px] font-medium tracking-[-.025em] text-[var(--accent)]">0{i + 1}</p>
              <div>
                <p className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">{article.tag} <span className="mx-1.5 text-[var(--line-strong)]">/</span> {article.readTime}</p>
                <h2 className="mt-4 max-w-2xl text-[clamp(1.35rem,2.2vw,1.8rem)] leading-[1.08] tracking-[-.05em] transition-colors group-hover:text-[var(--accent)]">{article.title}</h2>
                <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-[var(--ink-soft)]">{article.description}</p>
              </div>
              <span className="link-sweep flex w-fit items-center gap-1.5 text-[12px] font-medium tracking-[-.025em] text-[var(--accent)] sm:self-end sm:pb-1 group-hover:text-[var(--ink)]">Read <ArrowRight className="arrow-nudge size-3" strokeWidth={1.8} aria-hidden="true" /></span>
            </Link>
          </article>
        ))}
      </div>
    </section>
  </Page>;
}

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = ARTICLES.find((a) => a.slug === slug);

  // Fire the GA4 `article_read_complete` event once the reader scrolls the CTA
  // into view. Ported from the pre-redesign frontend/src/pages/ArticlePage.tsx;
  // the redesign rewrite of this component had dropped the observer, the CTA,
  // and the analytics import, so the event never fired (PR 22 review).
  const ctaRef = useRef<HTMLDivElement>(null);
  const trackedRef = useRef(false);
  useEffect(() => {
    trackedRef.current = false;
    const el = ctaRef.current;
    if (!el || !slug) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !trackedRef.current) {
        trackedRef.current = true;
        trackEvent("article_read_complete", { article_slug: slug });
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [slug]);

  // N-01: per-article SEO. The redesign left every article with a generic title,
  // description, and a canonical pointing at the homepage, and dropped the
  // markdown-alternate link. Set them per article here and restore them on
  // unmount so they don't leak onto other routes. The .md path mirrors what
  // scripts/generate-markdown.js writes: /education/{slug}.md.
  useEffect(() => {
    if (!article) return;
    const origin = window.location.origin;
    const canonicalUrl = `${origin}/education/${article.slug}`;
    const mdUrl = `${origin}/education/${article.slug}.md`;
    const prevTitle = document.title;
    document.title = `${article.title} — Lensy`;

    // Helper to upsert a <meta>/<link> and remember whether we created it.
    const upsert = (selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) => {
      let el = document.head.querySelector<HTMLElement>(selector);
      const created = !el;
      if (!el) { el = create(); document.head.appendChild(el); }
      const prev = el.getAttribute(el instanceof HTMLLinkElement ? "href" : "content");
      apply(el);
      return { el, created, prev };
    };

    const desc = upsert(
      'meta[name="description"]',
      () => { const m = document.createElement("meta"); m.setAttribute("name", "description"); return m; },
      (el) => el.setAttribute("content", article.description),
    );
    const canonical = upsert(
      'link[rel="canonical"]',
      () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; },
      (el) => el.setAttribute("href", canonicalUrl),
    );
    // The markdown-alternate link is article-specific, so always create a fresh
    // one tagged for cleanup rather than mutating a shared tag.
    const mdLink = document.createElement("link");
    mdLink.setAttribute("rel", "alternate");
    mdLink.setAttribute("type", "text/markdown");
    mdLink.setAttribute("href", mdUrl);
    mdLink.setAttribute("data-article-md", "true");
    document.head.appendChild(mdLink);

    return () => {
      document.title = prevTitle;
      // Restore or remove the meta/link tags we touched.
      if (desc.created) desc.el.remove(); else if (desc.prev !== null) desc.el.setAttribute("content", desc.prev);
      if (canonical.created) canonical.el.remove(); else if (canonical.prev !== null) canonical.el.setAttribute("href", canonical.prev);
      mdLink.remove();
    };
  }, [article]);

  if (!article) return <NotFound />;

  const idx = ARTICLES.indexOf(article);
  const prev = ARTICLES[idx - 1];
  const next = ARTICLES[idx + 1];

  return <>
    <header className="border-b border-[var(--line)] px-5 pb-12 pt-14 sm:px-8 sm:pb-16 sm:pt-20">
      <div className="mx-auto max-w-[760px]">
        <Link to="/education" className="inline-flex items-center gap-2 text-[12px] font-medium tracking-[-.025em] text-[var(--muted)] transition-colors hover:text-[var(--accent)]">
          <ArrowLeft className="size-3" strokeWidth={1.8} aria-hidden="true" /> Education
        </Link>
        <div className="mt-10 flex items-center gap-3 text-[12px] font-medium tracking-[-.025em] text-[var(--accent)]">
          <span>{article.tag}</span>
          <span className="text-[var(--line-strong)]">/</span>
          <span>{article.readTime}</span>
        </div>
        <h1 className="mt-5 text-[clamp(2.35rem,5vw,4.6rem)] font-medium leading-[.95] tracking-[-.065em]">{article.title}</h1>
        <p className="mt-7 max-w-[650px] text-[16px] leading-relaxed text-[var(--ink-soft)]">{article.description}</p>
      </div>
    </header>

    <div className="mx-auto max-w-[760px] px-5 sm:px-8">
      <div data-reveal className="py-12 sm:py-16">
        {article.body}
      </div>

      {/* CTA — also the scroll target that fires `article_read_complete`. */}
      <div ref={ctaRef} data-reveal className="rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] px-6 py-10 text-center sm:px-10 sm:py-12">
        <p className="mx-auto max-w-[440px] text-[clamp(1.35rem,2.6vw,1.9rem)] font-medium leading-tight tracking-[-.04em]">Check your documentation's AI readiness.</p>
        <Link to="/" className="btn-ink mt-7 inline-flex items-center gap-2 bg-[var(--panel-bg)] px-5 py-3 text-[13px] font-medium tracking-[-.02em] text-[var(--panel-fg)]">
          Try Lensy Free <ArrowRight className="arrow-nudge size-4" strokeWidth={1.8} aria-hidden="true" />
        </Link>
      </div>

      {(prev || next) && (
        <nav data-reveal className="grid gap-px border-t border-[var(--line)] py-10 sm:grid-cols-2">
          {prev ? (
            <Link to={`/education/${prev.slug}`} className="group flex flex-col gap-2 py-6 pr-8 transition-colors hover:text-[var(--accent)]">
              <span className="flex items-center gap-1.5 text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]"><ArrowLeft className="size-3" strokeWidth={1.8} aria-hidden="true" />Previous</span>
              <span className="text-[16px] leading-snug tracking-[-.03em]">{prev.title}</span>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/education/${next.slug}`} className="group flex flex-col gap-2 border-t border-[var(--line)] py-6 pl-0 text-right transition-colors hover:text-[var(--accent)] sm:border-l sm:border-t-0 sm:pl-8">
              <span className="flex items-center justify-end gap-1.5 text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">Next<ArrowRight className="size-3" strokeWidth={1.8} aria-hidden="true" /></span>
              <span className="text-[16px] leading-snug tracking-[-.03em]">{next.title}</span>
            </Link>
          ) : <div className="sm:border-l border-[var(--line)]" />}
        </nav>
      )}
    </div>
  </>;
}

// ---- Contact ----

export function Field({ label, hint, error, noBorder, children }: { label: string; hint?: string; error?: string; noBorder?: boolean; children: React.ReactNode }) {
  return <label className={`field-shell block px-3 py-5 transition-colors focus-within:border-[var(--accent)] ${noBorder ? '' : 'border-b border-[var(--line)]'}`}>
    <span className="flex items-baseline justify-between text-[12px] font-medium tracking-[-.025em] text-[var(--muted)]"><span>{label}</span>{hint && <span className="text-[10px] text-[var(--placeholder)]">{hint}</span>}</span>
    {children}
    {error && <span role="alert" className="audit-notice mt-3 block rounded-[var(--radius-xs)] bg-[var(--tint)] px-2.5 py-2 text-[11px] font-medium leading-relaxed tracking-[-.02em] text-[var(--ink)]">{error}</span>}
  </label>;
}

export function Contact() {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "default";
  // N-07: per-ref contact copy (restored from the old routed-out ContactPage).
  // ?ref=llmstxt / ?ref=markdown / ?ref=feedback each get their own heading,
  // intro, button and success text; anything else falls back to the waitlist.
  const REF_COPY: Record<string, { titleLead: string; titleAccent: string; intro: string; button: string; successTitle: string; successBody: string }> = {
    llmstxt: {
      titleLead: "Get your", titleAccent: "llms.txt.",
      intro: "We'll create a structured llms.txt file for your documentation so AI coding assistants like Cursor, Copilot, and Claude can discover and consume your docs faster.",
      button: "Request llms.txt", successTitle: "Request received.",
      successBody: "We'll review your docs and get back to you with a generated llms.txt file.",
    },
    markdown: {
      titleLead: "Get markdown", titleAccent: "versions.",
      intro: "We'll help you serve markdown alongside your HTML docs. AI agents consume markdown far more efficiently — fewer tokens, cleaner code generation.",
      button: "Request markdown", successTitle: "Request received.",
      successBody: "We'll review your docs and reach out about markdown generation.",
    },
    feedback: {
      titleLead: "Share your", titleAccent: "feedback.",
      intro: "Lensy is in beta and your input helps us improve. Tell us what worked, what didn't, or what you'd like to see next.",
      button: "Send feedback", successTitle: "Thank you.",
      successBody: "We read every submission and it directly shapes what we build.",
    },
    default: {
      titleLead: "Join the", titleAccent: "Waitlist.",
      intro: "Join the waitlist to get higher limits and get access to upcoming products.",
      button: "Join waitlist", successTitle: "You're on the list.",
      successBody: "We'll be in touch with early access details at the address you shared.",
    },
  };
  const refCopy = REF_COPY[ref] || REF_COPY.default;
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");
  const isFeedback = ref === "feedback";
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if ((!isFeedback && !website.trim()) || (isFeedback && !message.trim()) || !name.trim() || !email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      setFormError("validation");
      return;
    }
    setFormError("");
    setSending(true);
    try {
      const response = await fetch("https://www.perseveranceai.com/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ doc_url: website.trim(), name: name.trim(), email: email.trim(), organization: organization.trim(), message: message.trim() || undefined, ref }),
      });
      if (!response.ok) throw new Error(`Contact request failed with ${response.status}`);
      setSent(true);
    } catch (error) {
      console.error("[Contact] Submission failed", error);
      setFormError("We could not submit your request. Please try again or email hello@perseveranceai.com.");
    } finally {
      setSending(false);
    }
  };
  const websiteError = formError && !website.trim() ? "Add your developer portal or website URL to continue." : "";
  const nameError = formError && !name.trim() ? "Add your name so we know who to contact." : "";
  const emailError = formError && (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) ? "Enter a valid email address to join the waitlist." : "";
  const messageError = formError && !message.trim() ? "Share your feedback to continue." : "";
  const networkError = formError && formError !== "validation" ? formError : "";
  const inputCls = "mt-3 block w-full bg-transparent font-sans text-xl tracking-[-.035em] text-[var(--ink)] outline-none placeholder:text-[var(--placeholder)]";

  return <Page
    titleStyle={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
    title={<>{refCopy.titleLead}<br /><span className="text-[var(--accent)]">{refCopy.titleAccent}</span></>}
    intro={refCopy.intro}
  >
    <div className="grid gap-12 py-20 lg:grid-cols-12 lg:gap-16">
      <aside data-reveal className="flex flex-col gap-10 lg:col-span-4">
        <div>
          <p className="text-[12px] font-medium tracking-[-.025em] text-[var(--muted)]">Or schedule a demo</p>
          <a href="https://calendly.com/getperseverance" target="_blank" rel="noopener noreferrer" className="link-sweep mt-3 inline-flex items-center gap-2 text-[19px] tracking-[-.03em] text-[var(--accent)] hover:text-[var(--accent-hover)] transition-colors">
            Pick a time <ArrowRight className="arrow-nudge size-4" strokeWidth={1.8} aria-hidden="true" />
          </a>
        </div>
        <div>
          <p className="text-[12px] font-medium tracking-[-.025em] text-[var(--muted)]">Prefer email?</p>
          <a href="mailto:hello@perseveranceai.com" className="link-sweep mt-3 inline-block text-[17px] tracking-[-.03em] text-[var(--accent)]">hello@perseveranceai.com</a>
        </div>
      </aside>

      <div data-reveal style={{ "--reveal-delay": "120ms" } as React.CSSProperties} className="lg:col-span-7 lg:col-start-6">
        {sent ? (
          <div className="raised-shadow rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] p-8 [animation:scan-in_.45s_var(--ease)] sm:p-10">
            <span className="grid size-8 place-items-center rounded-[var(--radius-sm)] bg-[var(--accent)] text-[var(--accent-contrast)]"><Check className="size-4" strokeWidth={1.8} aria-hidden="true" /></span>
            <h2 className="mt-8 text-[28px] font-medium leading-tight tracking-[-.045em]">{refCopy.successTitle}</h2>
            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-[var(--ink-soft)]">{refCopy.successBody}</p>
            <button onClick={() => setSent(false)} className="mt-8 inline-flex items-center gap-2 border-b border-[var(--ink)] pb-1 text-[12px] font-medium tracking-[-.025em] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]">Submit another</button>
          </div>
        ) : (
          <form noValidate onSubmit={submit}>
            <div>
              {networkError && <p className="audit-notice mb-4 rounded-[var(--radius-xs)] bg-[var(--tint)] px-3 py-2.5 text-[12px] font-medium text-[var(--ink)]">{networkError}</p>}
              {!isFeedback && <Field label="Developer Portal or Website URL" error={websiteError}><input value={website} onChange={(event) => { setWebsite(event.target.value); setFormError(""); }} aria-invalid={Boolean(websiteError)} className={inputCls} placeholder="docs.yourcompany.com" /></Field>}
              <div className="sm:grid sm:grid-cols-2 sm:gap-x-8">
                <Field label="Name" error={nameError}><input value={name} onChange={(event) => { setName(event.target.value); setFormError(""); }} aria-invalid={Boolean(nameError)} className={inputCls} placeholder="Ada Lovelace" /></Field>
                <Field label="Email" error={emailError}><input value={email} onChange={(event) => { setEmail(event.target.value); setFormError(""); }} aria-invalid={Boolean(emailError)} type="email" className={inputCls} placeholder="you@company.com" /></Field>
              </div>
              <Field label="Organization / Company" noBorder={!isFeedback}><input value={organization} onChange={(event) => { setOrganization(event.target.value); setFormError(""); }} className={inputCls} placeholder="Acme Corp" /></Field>
              {isFeedback && <Field label="Your feedback" error={messageError} noBorder><textarea value={message} onChange={(event) => { setMessage(event.target.value); setFormError(""); }} aria-invalid={Boolean(messageError)} className={`${inputCls} min-h-32 resize-y`} placeholder="What worked, what didn’t, or what should we build next?" /></Field>}
            </div>
            <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-xs text-[11px] font-medium tracking-[-.025em] leading-relaxed text-[var(--muted)]">Your inbox is safe with us. We only use this to get in touch.</p>
              <button disabled={sending} className="btn-ink group inline-flex items-center gap-2 bg-[var(--panel-bg)] px-5 py-3 text-[12px] font-medium tracking-[-.025em] text-[var(--panel-fg)] disabled:cursor-wait disabled:opacity-70">{sending ? "Sending…" : <>{refCopy.button} <ArrowRight className="arrow-nudge size-3.5" strokeWidth={1.8} aria-hidden="true" /></>}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  </Page>;
}

// ---- Legal ----

export function LegalPage({ title, updated, children }: { title: string; updated: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  const isTerms = pathname === "/terms";
  return <>
    <header className="mx-auto grid max-w-[1240px] gap-8 border-b border-[var(--line)] px-5 pb-14 pt-16 sm:px-8 sm:pb-20 sm:pt-24 lg:grid-cols-12">
      <div className="lg:col-span-3"><Link to="/" className="inline-flex items-center gap-2 text-[12px] font-medium tracking-[-.025em] text-[var(--muted)] transition-colors hover:text-[var(--accent)]"><ArrowLeft className="size-3" strokeWidth={1.8} aria-hidden="true" />Back to Lensy</Link><p className="mt-7 text-[12px] font-medium tracking-[-.025em] text-[var(--accent)]">Legal documents</p></div>
      <div className="lg:col-span-6"><h1 className="text-[clamp(3.25rem,6.4vw,6.25rem)] font-medium leading-[.88] tracking-[-.075em]">{title}</h1><p className="mt-6 max-w-md text-[15px] leading-relaxed text-[var(--ink-soft)]">The terms that explain how Perseverance AI and Lensy are offered, and how information is handled.</p></div>
      <div className="self-end border-l border-[var(--line)] pl-4 text-[11px] font-medium tracking-[-.025em] leading-relaxed text-[var(--muted)] lg:col-span-3"><span className="block">Last updated</span><span className="mt-1 block text-[var(--ink-soft)]">{updated}</span></div>
    </header>
    <section className="mx-auto grid max-w-[1240px] gap-10 px-5 py-12 sm:px-8 sm:py-16 lg:grid-cols-12 lg:gap-16">
      <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start">
        <p className="text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]">In this section</p>
        <nav className="mt-4 flex border-y border-[var(--line)] py-1.5 lg:flex-col" aria-label="Legal documents">
          <Link to="/terms" aria-current={isTerms ? "page" : undefined} className={`rounded-[var(--radius-xs)] px-3 py-2.5 text-[13px] transition-colors ${isTerms ? "bg-[var(--surface-2)] text-[var(--ink)]" : "text-[var(--ink-soft)] hover:bg-[var(--surface)] hover:text-[var(--ink)]"}`}>Terms of Use</Link>
          <Link to="/privacy" aria-current={!isTerms ? "page" : undefined} className={`rounded-[var(--radius-xs)] px-3 py-2.5 text-[13px] transition-colors ${!isTerms ? "bg-[var(--surface-2)] text-[var(--ink)]" : "text-[var(--ink-soft)] hover:bg-[var(--surface)] hover:text-[var(--ink)]"}`}>Privacy Policy</Link>
        </nav>
        <p className="mt-7 max-w-[15rem] text-[12px] leading-relaxed text-[var(--muted)]">Questions about this document? <a href="mailto:hello@perseveranceai.com" className="text-[var(--ink-soft)] underline underline-offset-2 hover:text-[var(--accent)]">Get in touch</a>.</p>
      </aside>
      <article key={pathname} data-reveal className="surface-shadow rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] p-6 sm:p-10 lg:col-span-7 lg:col-start-5">
        <div className="flex items-center justify-between border-b border-[var(--line)] pb-4 text-[11px] font-medium tracking-[-.025em] text-[var(--muted)]"><span>Perseverance AI</span><span>{isTerms ? "Terms" : "Privacy"}</span></div>
        <div className="legal-prose prose-lensy pt-8">{children}</div>
      </article>
    </section>
  </>;
}

export function Terms() {
  return <LegalPage title="Terms of Use" updated="February 10, 2026">
    <p>These Terms of Use ("Terms") govern your access to and use of the Perseverance AI Console and all associated services, including Lensy ("Services"), operated by Perseverance AI ("Company", "we", "our", or "us"). By accessing or using the Services, you agree to be bound by these Terms.</p>

    <h2>1. Beta Program &amp; Access</h2>
    <p>The Services are in private beta. Access is granted on an invitation-only basis via access codes. Your access code is personal, non-transferable, and confidential. Keep your access code private. We can revoke access at any time.</p>

    <h2>2. Confidentiality</h2>
    <p>As a beta participant, you acknowledge that the Services, including their features, functionality, design, user interface, architecture, and all related documentation, constitute confidential and proprietary information of Perseverance AI. You agree to:</p>
    <ul>
      <li>Not disclose, publish, or share any information about the Services with any third party</li>
      <li>Not take screenshots, recordings, or other reproductions of the Services for distribution</li>
      <li>Not discuss the features, capabilities, or design of the Services publicly (including social media, forums, or blog posts)</li>
      <li>Provide feedback only through authorized channels (hello@perseveranceai.com)</li>
    </ul>

    <h2>3. Intellectual Property</h2>
    <p>All intellectual property rights in and to the Services — including but not limited to the software, algorithms, user interface design, visual design, branding, logos, documentation, and all underlying technology — are and shall remain the exclusive property of Perseverance AI. Nothing in these Terms grants you any right, title, or interest in our intellectual property beyond the limited right to use the Services during the beta period.</p>
    <p>Do not:</p>
    <ul>
      <li>Copy, reproduce, modify, or create derivative works based on the Services</li>
      <li>Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code or underlying technology</li>
      <li>Replicate, imitate, or create competing products or services based on the concepts, features, or design of the Services</li>
      <li>Remove, alter, or obscure any copyright, trademark, or proprietary notices</li>
      <li>Use any proprietary information to build a similar or competing service</li>
    </ul>

    <h2>4. Acceptable Use</h2>
    <p>You agree to use the Services only for their intended purpose — evaluating and improving documentation quality. You shall not:</p>
    <ul>
      <li>Use the Services for any unlawful purpose or in violation of any applicable law</li>
      <li>Attempt to gain unauthorized access to any systems or networks</li>
      <li>Interfere with or disrupt the integrity or performance of the Services</li>
      <li>Use the Services to process, store, or transmit malicious code</li>
      <li>Resell, sublicense, or commercially exploit the Services without prior written consent</li>
    </ul>

    <h2>5. Data &amp; Privacy</h2>
    <p>Our collection and use of information is described in our <Link to="/privacy" className="text-[var(--accent)] underline underline-offset-2">Privacy Policy</Link>. By using the Services, you consent to such collection and use.</p>

    <h2>6. Disclaimer of Warranties</h2>
    <p>We provide the Services "as is" and "as available" without any warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the Services will be uninterrupted, error-free, or secure.</p>

    <h2>7. Limitation of Liability</h2>
    <p>To the maximum extent permitted by law, Perseverance AI shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or business opportunities arising out of or related to your use of the Services.</p>

    <h2>8. Termination</h2>
    <p>We may terminate or suspend your access to the Services immediately, without prior notice, for any reason, including without limitation if you breach these Terms. Upon termination, your right to use the Services will cease immediately. Sections 2 (Confidentiality), 3 (Intellectual Property), 6, 7, and 9 shall survive termination.</p>

    <h2>9. Governing Law</h2>
    <p>These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Delaware.</p>

    <h2>10. Changes to Terms</h2>
    <p>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the Services after any changes constitutes acceptance of the revised Terms.</p>

    <h2>Contact</h2>
    <p>Questions about these Terms? Contact us at <a href="mailto:hello@perseveranceai.com" className="text-[var(--accent)] underline underline-offset-2">hello@perseveranceai.com</a></p>
    <p className="text-[var(--muted)]">© 2026 Perseverance AI. All rights reserved. Confidential &amp; Proprietary.</p>
  </LegalPage>;
}

export function Privacy() {
  // Every statement here was checked against the code (backend/lib/lensy-stack.ts,
  // backend/lambda, public/index.html). If you change what Lensy collects, sends
  // or keeps, change this page in the same PR.
  return <LegalPage title="Privacy Policy" updated="October 22, 2026">
    <p>This policy explains what Perseverance AI ("we", "us") collects when you use Lensy on perseveranceai.com, who we share it with, and how long we keep it.</p>

    <h2>1. What we collect</h2>
    <ul>
      <li><strong>Pages you scan:</strong> the URL you submit. To analyse it, Lensy fetches that page and public files on the same site, such as <code>robots.txt</code>, <code>llms.txt</code> and the sitemap. We store the page content we read and the report we produce.</li>
      <li><strong>Contact form:</strong> your name, email, organization, docs URL and message, if you send them.</li>
      <li><strong>Feedback:</strong> your message, the page you were on and, if you add it, your email.</li>
      <li><strong>Usage limits:</strong> to apply the free daily scan limit, we store a hashed form of your IP address with a count of today&apos;s scans.</li>
      <li><strong>Server logs:</strong> our servers log requests, including your IP address, browser type, the URL you scanned and the time.</li>
      <li><strong>Analytics, only if you accept:</strong> which pages you visit and which buttons you use, such as starting a scan or downloading a report, collected by Google Analytics.</li>
    </ul>

    <h2>2. Cookies and browser storage</h2>
    <p>If you accept analytics in the cookie banner, Google Analytics sets the <code>_ga</code> and <code>_ga_*</code> cookies to count visits. If you decline or don&apos;t choose, Google Analytics does not load and these cookies are not set. You can change your choice at any time with <strong>Cookie settings</strong> in the footer.</p>
    <p>Lensy also keeps a few things in your browser that are needed for the site to work, not for tracking: your theme, your cookie choice, and your latest scan, which is cleared when you close the tab.</p>

    <h2>3. How we use it</h2>
    <ul>
      <li>To run scans and show you the report</li>
      <li>To apply the free daily scan limit and stop abuse</li>
      <li>To answer your messages and feedback</li>
      <li>To understand how Lensy is used and improve it</li>
      <li>To find and fix problems</li>
    </ul>

    <h2>4. Who we share it with</h2>
    <p>We do not sell or rent your information. We use these services to run Lensy, and each one gets only what it needs:</p>
    <ul>
      <li><strong>Amazon Web Services:</strong> hosting, storage and email. Data is stored in the United States (AWS us-east-1). Page content is analysed by AI models through Amazon Bedrock.</li>
      <li><strong>Jina AI (Jina Reader):</strong> when a page only shows its content with JavaScript and you choose to render it, we send its URL to Jina Reader, which loads the page and returns the text.</li>
      <li><strong>Perplexity:</strong> when the AI citation check runs, we send search questions about the scanned product to Perplexity to see whether AI answers cite the page.</li>
      <li><strong>Google:</strong> Google Analytics, only if you accept. Our fonts load from Google Fonts, which receives your IP address when your browser requests them.</li>
    </ul>
    <p>We may also share information if the law requires it, or with a buyer if the company is sold.</p>

    <h2>5. How long we keep it</h2>
    <ul>
      <li>Scan files (page content and reports) are deleted automatically about 30 days after they are created. Cached summaries of scanned pages expire after 7 days.</li>
      <li>The hashed IP address used for the daily limit expires after 48 hours.</li>
      <li>A short record of each scan (the URL you entered and the URL it led to, the time, the status code the site returned, the result, the score, the hashed form of your IP address, and whether it came from our website or a test) is kept so we can see how Lensy is used.</li>
      <li>Contact form entries and feedback are kept until you ask us to delete them.</li>
      <li>Server logs do not yet expire on a fixed schedule.</li>
      <li>Google Analytics data is kept under the retention setting of our Google Analytics account.</li>
    </ul>

    <h2>6. Security</h2>
    <p>Data is sent over HTTPS and stored encrypted on AWS. No system is perfectly secure.</p>

    <h2>7. Your rights</h2>
    <p>You can ask us what we hold about you, or ask us to correct or delete it. Email <a href="mailto:hello@perseveranceai.com" className="text-[var(--accent)] underline underline-offset-2">hello@perseveranceai.com</a>.</p>

    <h2>8. Children</h2>
    <p>Lensy is not meant for anyone under 18, and we do not knowingly collect their information.</p>

    <h2>9. Changes</h2>
    <p>When we change this policy, we update the date at the top of this page.</p>

    <h2>Contact</h2>
    <p>Questions about this policy? Email <a href="mailto:hello@perseveranceai.com" className="text-[var(--accent)] underline underline-offset-2">hello@perseveranceai.com</a>.</p>
    <p className="text-[var(--muted)]">© 2026 Perseverance AI. All rights reserved. Confidential &amp; Proprietary.</p>
  </LegalPage>;
}

// /bot: the page Lensy's user agent points to (spec docs/two-score-readiness §7.5).
// Every statement here matches backend/lambda/agent-handler/shared/fetch-page.ts.
export function BotInfo() {
  const code = "rounded-[var(--radius-xs)] bg-[var(--surface-2)] px-1.5 py-0.5 text-[13px]";
  const userAgent = "Lensy/1.0 (+https://perseveranceai.com/bot)";
  // The scan IP differs per environment and is only known after the stack deploys, so it comes
  // from the API at runtime, never from the build.
  const [egressIps, setEgressIps] = useState<string[] | null>(null);
  const [ipError, setIpError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/bot-info`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!cancelled) setEgressIps(Array.isArray(d?.egressIps) ? d.egressIps : []); })
      .catch(() => { if (!cancelled) setIpError(true); });
    return () => { cancelled = true; };
  }, []);
  const ipList = egressIps && egressIps.length > 0 ? egressIps : null;
  const ipForRule = ipList ? ipList.join(" ") : "<Lensy IP>";
  return <Page title={<>Lensy<br /><span className="text-[var(--accent)]">checker.</span></>} intro="What the Lensy checker is, when it visits your site, and how to let it through your bot protection safely.">
    <article className="legal-prose prose-lensy max-w-[760px] py-12 sm:py-16">
      <h2>What it is</h2>
      <p>Lensy checks whether a documentation page is ready for AI search engines and coding agents. When someone enters a URL on perseveranceai.com, Lensy fetches that page and a few public files on the same site, such as <code className={code}>robots.txt</code>, <code className={code}>llms.txt</code>, the sitemap and the page&apos;s Markdown version. That&apos;s usually 10 to 25 requests per scan. It does not crawl your site on its own; each visit starts from a person&apos;s scan.</p>

      <h2>How to recognise it</h2>
      <p>Lensy names itself in every request:</p>
      <p><code className={code} data-testid="bot-user-agent">{userAgent}</code></p>
      <p>Every scan comes from this IP address:</p>
      <p data-testid="bot-egress-ips">
        {ipList ? ipList.map((ip) => <code key={ip} className={`${code} mr-2`}>{ip}</code>)
          : ipError ? <span>We couldn&apos;t load the address just now. Please reload, or email us and we&apos;ll send it.</span>
          : <span className="text-[var(--muted)]">Loading…</span>}
      </p>
      <p>We&apos;ll announce any change to this address on this page at least 14 days ahead.</p>
      <p>If the site refuses the first request with HTTP 403 or 429, Lensy tries once more with a browser user agent that ends with the same <code className={code}>(+https://perseveranceai.com/bot)</code> link, from the same IP. If that is refused too, the scan stops and tells the person that the site&apos;s bot protection blocked the checker.</p>
      <p>Lensy never sends the names of other companies&apos; crawlers, such as GPTBot or ClaudeBot.</p>
      <p>The open-source Lensy command-line tool identifies itself as <code className={code}>Lensy-CLI</code>. It runs on the user&apos;s own machine or CI runner, so it has no fixed IP.</p>

      <h2>robots.txt</h2>
      <p>A Lensy scan is a fetch a person asked for, like an AI assistant opening a page on a user&apos;s behalf, so Lensy doesn&apos;t apply your robots.txt rules to itself. It reads robots.txt to report what your rules tell AI bots. To stop Lensy visiting, use one of the options under &ldquo;How to block it&rdquo;.</p>

      <h2>How to allow it</h2>
      <p>Match <strong>both</strong> the user agent and the IP address. A rule on the user agent alone lets in anyone who copies the name.</p>
      <p><strong>Cloudflare Pro and above:</strong> add a custom WAF rule with the action <strong>Skip</strong> for this expression, and choose to skip Super Bot Fight Mode, the managed rules, Browser Integrity Check and Security Level:</p>
      <p><code className={code} data-testid="bot-allow-rule">{`(http.user_agent contains "Lensy/1.0") and (ip.src in {${ipForRule}})`}</code></p>
      <p data-testid="bot-free-plan"><strong>Cloudflare free plan:</strong> Bot Fight Mode can&apos;t be skipped by a custom rule. Instead, add an <strong>IP Access rule</strong> with the action <strong>Allow</strong> for {ipList ? ipList.join(", ") : "the IP above"}; Bot Fight Mode doesn&apos;t act on requests an IP Access rule allows. This matches the IP only, which is reasonable here because that address is dedicated to Lensy and used by nothing else.</p>
      <p>Other firewalls and CDNs have an equivalent &ldquo;allow when user agent and source IP match&rdquo; rule.</p>
      <p>You don&apos;t have to do this. A blocked scan doesn&apos;t mean verified AI crawlers are blocked, and we&apos;re working on getting Lensy onto Cloudflare&apos;s verified-bot list so it works without any change on your side.</p>

      <h2>How to block it</h2>
      <p>Block requests that match the user agent and IP above in your firewall, or email us and we&apos;ll stop scans of your domain.</p>

      <h2>Contact</h2>
      <p>Questions, or want Lensy to stop visiting your site? Email <a href="mailto:hello@perseveranceai.com" className="text-[var(--accent)] underline underline-offset-2">hello@perseveranceai.com</a>.</p>
    </article>
  </Page>;
}

// ---- Shared ----

export function Page({ title, intro, children, titleSize = "", titleStyle = { fontSize: "clamp(3rem, 7vw, 6.5rem)" } }: { title: React.ReactNode; intro: string; children: React.ReactNode; titleSize?: string; titleStyle?: React.CSSProperties }) {
  return <>
    <header className="mx-auto grid max-w-[1240px] gap-8 border-b border-[var(--line)] px-5 pb-16 pt-20 sm:px-8 lg:grid-cols-12 lg:pb-20">
      <h1 style={titleStyle} className={`hero-intro ${titleSize} font-medium leading-[.88] tracking-[-.075em] lg:col-span-8`}>{title}</h1>
      <p className="self-end text-[15px] leading-relaxed text-[var(--ink-soft)] lg:col-span-4">{intro}</p>
    </header>
    <div className="mx-auto max-w-[1240px] px-5 sm:px-8">{children}</div>
  </>;
}

export function NotFound() {
  useNoindex();
  return <Page title={<>Nothing<br /><span className="text-[var(--accent)]">here.</span></>} intro="This page does not exist.">
    <Link className="btn-ink my-16 inline-block bg-[var(--panel-bg)] px-4 py-3 text-[12px] font-medium tracking-[-.025em] text-[var(--panel-fg)]" to="/">Back to Lensy</Link>
  </Page>;
}

export const router = createBrowserRouter([{
  path: "/",
  Component: Shell,
  children: [
    { index: true, Component: Home },
    { path: "scan", Component: ScanReport },
    { path: "how-it-works", Component: HowItWorks },
    { path: "education", Component: Education },
    { path: "education/:slug", Component: ArticlePage },
    { path: "contact", Component: Contact },
    { path: "terms", Component: Terms },
    { path: "privacy", Component: Privacy },
    { path: "bot", Component: BotInfo },
    { path: "*", Component: NotFound },
  ],
}]);
